import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import InlineCategorySelect from '../components/InlineCategorySelect';
import BankRulesStatusBar from '../components/banking/BankRulesStatusBar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { HelpPanel } from '../components/ui/help-panel';
import { bankingHelp } from '../lib/helpContent';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { SearchableAccountSelect } from '../components/SearchableAccountSelect';
import { 
  Search, 
  ArrowUpRight, 
  ArrowDownLeft, 
  ArrowLeftRight,
  ChevronRight, 
  Zap,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  FileText,
  Loader2,
  Tags,
  PlusCircle,
  Link2,
  CheckCircle2,
  CheckCircle,
  Check,
  Sparkles,
  Building2,
  RefreshCw,
  MoreHorizontal,
  Archive,
  Ban,
  Undo2,
  ArrowUpDown,
  CalendarIcon,
  Filter,
  X,
  CheckSquare,
  Paperclip,
  MessageSquare,
  Clock,
  SplitSquareVertical,
  AlertCircle,
  Wallet,
  CreditCard,
  Landmark,
  HandCoins,
  CircleDollarSign,
  HelpCircle,
  Eye,
  EyeOff,
  Upload,
  GripVertical,
  Hash,
  Calendar as CalendarDays,
  CreditCard as CheckNumber,
  ScanLine,
  Wand2
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { format, startOfMonth } from 'date-fns';
import { bankLogoFallback, formatRelativeTime } from '../lib/bankLogoFallback';
import Attachments from './Attachments';
import DocumentScanner from '../components/DocumentScanner';
import RelinkBankBanner from '../components/RelinkBankBanner';
import BrandEmptyState from '../components/landing/BrandEmptyState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(Math.abs(amount));
};

// Format date to MM/DD/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    return format(date, 'MM/dd/yyyy');
  } catch {
    return dateStr;
  }
};

// Transaction Status Badge
// Takes both status AND suggested_account_id to accurately determine if truly categorized
const TransactionStatusBadge = ({ status, hasCategoryAssigned }) => {
  const config = {
    pending: { label: 'Pending', class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
    for_review: { label: 'For Review', class: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
    categorized: { label: 'Categorized', class: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
    matched: { label: 'Matched', class: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400' },
    posted: { label: 'Posted', class: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' },
    excluded: { label: 'Excluded', class: 'bg-gray-100 text-gray-800 dark:bg-gray-700/50 dark:text-gray-200' },
    uncategorized: { label: 'Uncategorized', class: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  };
  
  // Determine effective status:
  // If status is "categorized" but no category is actually assigned, show "Uncategorized"
  let effectiveStatus = status;
  if (status === 'categorized' && !hasCategoryAssigned) {
    effectiveStatus = 'uncategorized';
  }
  // Also treat "for_review" without category as "uncategorized" for clearer UX
  if (status === 'for_review' && !hasCategoryAssigned) {
    effectiveStatus = 'uncategorized';
  }
  
  const cfg = config[effectiveStatus] || config.for_review;
  
  return (
    <Badge variant="secondary" className={cn('text-xs font-medium', cfg.class)} data-testid={`status-badge-${effectiveStatus}`}>
      {cfg.label}
    </Badge>
  );
};

// AI Buck Confidence Pill - follows AI_BUCK_GUIDELINES.md
const ConfidencePill = ({ confidence }) => {
  if (!confidence) return null;
  
  const pct = Math.round(confidence * 100);
  // High (≥85%): Green - AI Buck is confident
  // Medium (70-84%): Amber - AI Buck suggests reviewing
  // Low (<70%): Red - AI Buck needs your input
  let color = 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400';
  let label = 'Low';
  if (pct >= 85) {
    color = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
    label = 'High';
  } else if (pct >= 70) {
    color = 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
    label = 'Medium';
  }
  
  return (
    <Badge variant="secondary" className={cn('text-xs', color)} title={`AI Buck Confidence: ${label}`}>
      {pct}%
    </Badge>
  );
};

// Sync is stale when the last pull is older than 36h (Plaid refreshes daily)
const isSyncStale = (lastSync) => {
  if (!lastSync) return true;
  return (Date.now() - new Date(lastSync).getTime()) > 36 * 3600 * 1000;
};

// Account Carousel Card
const AccountCard = ({ account, isSelected, onSelect, onArchive, onSyncNow }) => {
  const isManual = account.source === 'manual';
  const [plaidLogoFailed, setPlaidLogoFailed] = useState(false);
  const [fallbackLogoFailed, setFallbackLogoFailed] = useState(false);

  // Plaid returns the institution's brand color (e.g. "#005EB8" Huntington
  // green). Falls back to the brand emerald when missing. Render as a
  // 3-px left rail so the card visually pops to the correct bank at a glance.
  const accentColor = !isManual && account.institution_primary_color
    ? account.institution_primary_color
    : null;

  // Friendly initials monogram for manual / logo-less accounts so every
  // card has a visual mark, not just a generic Wallet/Building icon.
  const initials = (account.institution_name || account.account_name || 'B')
    .split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || 'B';

  // Logo fallback chain:
  //   1. Plaid-provided base64/URL (account.institution_logo_url)
  //   2. Google favicon service via bankLogoFallback() — tries
  //      institution_name, legal_name, account_name so Chase-style
  //      accounts with names like "BUS COMPLETE CHE..." still resolve
  //   3. Initials monogram (last resort, manual accounts always land here)
  // Each stage fails independently: a broken Plaid URL falls through to
  // the favicon fallback instead of straight to the monogram. Manual
  // accounts also get the name-based lookup (e.g. a "JPMorgan Chase"
  // statement upload still shows the Chase logo).
  const fallbackLogo = !fallbackLogoFailed
    ? bankLogoFallback(
        account.institution_name,
        account.legal_name,
        account.account_name,
        account.name,
      )
    : null;
  const plaidLogo = !plaidLogoFailed ? account.institution_logo_url : null;
  const resolvedLogo = plaidLogo || fallbackLogo;

  return (
    <div
      className={cn(
        "group relative flex-shrink-0 w-56 rounded-lg border transition-all overflow-hidden",
        isSelected
          ? "border-primary bg-primary/5 ring-1 ring-primary"
          : "border-border bg-card hover:border-primary/50",
        account.is_archived && "opacity-60",
      )}
      style={accentColor ? { borderLeft: `3px solid ${accentColor}` } : undefined}
      data-testid={`bank-card-${account.bank_id}`}
    >
      <button
        type="button"
        onClick={() => onSelect(account.bank_id)}
        className="w-full text-left p-3 pl-3.5"
      >
      <div className="flex items-start justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          {resolvedLogo ? (
            <img
              src={resolvedLogo}
              alt={account.institution_name || 'Bank'}
              className="w-5 h-5 rounded object-contain bg-white p-0.5"
              onError={() => {
                if (plaidLogo) setPlaidLogoFailed(true);
                else setFallbackLogoFailed(true);
              }}
            />
          ) : isManual ? (
            <span
              className="w-5 h-5 rounded flex items-center justify-center text-[9px] font-bold text-white bg-gradient-to-br from-amber-500 to-orange-600"
              aria-hidden
            >
              {initials}
            </span>
          ) : (
            <span
              className="w-5 h-5 rounded flex items-center justify-center text-[9px] font-bold text-white bg-gradient-to-br from-blue-500 to-indigo-600"
              aria-hidden
            >
              {initials}
            </span>
          )}
          <span className="font-medium text-xs truncate max-w-[100px]">
            {account.institution_name}
          </span>
          {isManual && (
            <Badge variant="outline" className="text-[9px] px-1 py-0 bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800">
              Manual
            </Badge>
          )}
          {account.is_archived && (
            <Badge variant="outline" className="text-[9px] px-1 py-0">
              Archived
            </Badge>
          )}
        </div>
        {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-primary mr-6" />}
      </div>
      <p className="text-xs text-muted-foreground truncate mb-1">
        {account.account_name} {account.mask && `••••${account.mask}`}
      </p>
      <p className="text-base font-mono font-bold">
        {formatCurrency(account.current_balance)}
      </p>
      <div className="flex items-center gap-1.5 mt-1.5">
        {account.pending_count > 0 && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
            {account.pending_count} pending
          </Badge>
        )}
      </div>
      {/* Last-synced timestamp for manual accounts (Plaid accounts get the
          footer row with the always-visible Sync button instead). */}
      {isManual && account.last_sync && (
        <p className="text-[10px] text-muted-foreground mt-1 truncate">
          Synced {formatRelativeTime(account.last_sync)}
        </p>
      )}
      {/* Balance-pull error from Plaid (e.g. ITEM_LOGIN_REQUIRED). Tells
          the user why their balance isn't matching their bank app even
          though "Sync" reported success. */}
      {account.balance_error && (
        <p className="text-[10px] text-amber-700 dark:text-amber-400 mt-1 truncate" title={account.balance_error}>
          ⚠️ Balance stale — reconnect bank
        </p>
      )}
      </button>
      {/* Always-visible sync row for Plaid accounts — sync must never hide
          behind a hover menu or the selected checkmark. */}
      {!isManual && !account.is_archived && onSyncNow && (
        <div className="flex items-center justify-between gap-2 px-3.5 pb-2.5 -mt-1.5">
          <span
            className={cn(
              "text-[10px] truncate",
              isSyncStale(account.last_sync)
                ? "text-amber-600 dark:text-amber-400 font-medium"
                : "text-muted-foreground"
            )}
            data-testid={`bank-card-sync-status-${account.bank_id}`}
          >
            {account.last_sync ? `Synced ${formatRelativeTime(account.last_sync)}` : 'Never synced'}
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onSyncNow(account); }}
            className="flex items-center gap-1 text-[10px] font-medium text-primary border border-primary/30 rounded-full px-2 py-0.5 hover:bg-primary/10 transition-colors flex-shrink-0"
            data-testid={`bank-card-sync-btn-${account.bank_id}`}
            aria-label={`Sync ${account.institution_name || 'account'} now`}
          >
            <RefreshCw className="w-2.5 h-2.5" /> Sync
          </button>
        </div>
      )}
      {/* Hover-revealed actions kebab — archive / unarchive */}
      {onArchive && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="absolute top-1 right-1 p-1 rounded-md bg-background/70 opacity-60 hover:opacity-100 focus:opacity-100 hover:bg-background transition-opacity"
              onClick={(e) => e.stopPropagation()}
              data-testid={`bank-card-menu-${account.bank_id}`}
              aria-label="Account actions"
            >
              <MoreHorizontal className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            {!isManual && !account.is_archived && (
              <DropdownMenuItem
                onClick={() => onSyncNow(account)}
                data-testid={`bank-card-sync-${account.bank_id}`}
              >
                <RefreshCw className="w-3.5 h-3.5 mr-2" /> Sync now
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => onArchive(account)}
              data-testid={`bank-card-archive-${account.bank_id}`}
            >
              {account.is_archived ? (
                <><Undo2 className="w-3.5 h-3.5 mr-2" /> Restore from archive</>
              ) : (
                <><Archive className="w-3.5 h-3.5 mr-2" /> Archive account</>
              )}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

// Sortable Column Header
const SortableHeader = ({ label, field, currentSort, currentDir, onSort, className, align = 'left' }) => {
  const isActive = currentSort === field;
  
  return (
    <TableHead 
      className={cn("cursor-pointer select-none hover:bg-muted/50 whitespace-nowrap", className)}
      onClick={() => onSort(field)}
    >
      <div className={cn("flex items-center gap-1", align === 'right' && "justify-end")}>
        <span className="text-xs font-medium">{label}</span>
        {isActive ? (
          currentDir === 'asc' 
            ? <ChevronUp className="w-3 h-3 text-primary" />
            : <ChevronDown className="w-3 h-3 text-primary" />
        ) : (
          <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
        )}
      </div>
    </TableHead>
  );
};

// Status options for filter
const STATUS_OPTIONS = [
  { value: 'for_review', label: 'For Review', color: 'amber' },
  { value: 'categorized', label: 'Categorized', color: 'blue' },
  { value: 'posted', label: 'Posted', color: 'emerald' },
  { value: 'excluded', label: 'Excluded', color: 'gray' },
];

// Status Filter Header Component
const StatusFilterHeader = ({ 
  selected, 
  onApply, 
  currentSort, 
  currentDir, 
  onSort,
  open,
  onOpenChange 
}) => {
  const [localSelected, setLocalSelected] = useState([]);
  const isActive = currentSort === 'status';
  const hasFilter = selected.length > 0;
  
  // Initialize local state when popover opens
  const handleOpenChange = (isOpen) => {
    if (isOpen) {
      setLocalSelected([...selected]);
    }
    onOpenChange(isOpen);
  };
  
  const handleSelectAll = () => {
    setLocalSelected(STATUS_OPTIONS.map(s => s.value));
  };
  
  const handleClear = () => {
    setLocalSelected([]);
  };
  
  const handleApply = () => {
    onApply(localSelected);
    onOpenChange(false);
  };
  
  const toggleStatus = (value) => {
    setLocalSelected(prev => 
      prev.includes(value) 
        ? prev.filter(v => v !== value)
        : [...prev, value]
    );
  };
  
  return (
    <TableHead className="w-24">
      <div className="flex items-center gap-1">
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-auto p-0 hover:bg-transparent font-medium text-xs",
                hasFilter && "text-primary"
              )}
              title="Filter the table to specific workflow states"
              data-testid="status-filter-btn"
            >
              <span>Status</span>
              {hasFilter && (
                <Badge variant="secondary" className="ml-1 text-[9px] px-1 py-0 h-4">
                  {selected.length}
                </Badge>
              )}
              <Filter className={cn("w-3 h-3 ml-1", hasFilter ? "text-primary" : "text-muted-foreground/50")} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-0" align="start">
            <div className="p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium">Filter by Status</span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={handleSelectAll}>
                    All
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={handleClear}>
                    Clear
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                {STATUS_OPTIONS.map((status) => (
                  <label
                    key={status.value}
                    className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer"
                  >
                    <Checkbox
                      checked={localSelected.includes(status.value)}
                      onCheckedChange={() => toggleStatus(status.value)}
                      data-testid={`status-checkbox-${status.value}`}
                    />
                    <span className={cn(
                      "text-xs",
                      status.color === 'amber' && "text-amber-600",
                      status.color === 'blue' && "text-blue-600",
                      status.color === 'emerald' && "text-emerald-600",
                      status.color === 'gray' && "text-gray-500"
                    )}>
                      {status.label}
                    </span>
                  </label>
                ))}
              </div>
              <Button size="sm" className="w-full h-7 text-xs" onClick={handleApply} data-testid="status-filter-apply">
                Apply
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        <Button
          variant="ghost"
          size="sm"
          className="h-auto p-0 hover:bg-transparent"
          onClick={() => onSort('status')}
        >
          {isActive ? (
            currentDir === 'asc' 
              ? <ChevronUp className="w-3 h-3 text-primary" />
              : <ChevronDown className="w-3 h-3 text-primary" />
          ) : (
            <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
          )}
        </Button>
      </div>
    </TableHead>
  );
};

// Description Filter Header Component
const DescriptionFilterHeader = ({ 
  searchValue,
  onSearchChange,
  selectedVendors,
  onVendorsChange,
  topVendors,
  currentSort, 
  currentDir, 
  onSort,
  open,
  onOpenChange,
  onApply
}) => {
  const [localSearch, setLocalSearch] = useState('');
  const [localVendors, setLocalVendors] = useState([]);
  const isActive = currentSort === 'name';
  const hasFilter = searchValue || selectedVendors.length > 0;
  
  // Initialize local state when popover opens
  const handleOpenChange = (isOpen) => {
    if (isOpen) {
      setLocalSearch(searchValue);
      setLocalVendors([...selectedVendors]);
    }
    onOpenChange(isOpen);
  };
  
  const handleClear = () => {
    setLocalSearch('');
    setLocalVendors([]);
  };
  
  const handleApply = () => {
    onSearchChange(localSearch);
    onVendorsChange(localVendors);
    onApply();
    onOpenChange(false);
  };
  
  const toggleVendor = (vendor) => {
    setLocalVendors(prev => 
      prev.includes(vendor) 
        ? prev.filter(v => v !== vendor)
        : [...prev, vendor]
    );
  };
  
  return (
    <TableHead className="min-w-[200px]">
      <div className="flex items-center gap-1">
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-auto p-0 hover:bg-transparent font-medium text-xs",
                hasFilter && "text-primary"
              )}
              title="Filter by vendor or keyword"
              data-testid="description-filter-btn"
            >
              <span>Description</span>
              {hasFilter && (
                <Badge variant="secondary" className="ml-1 text-[9px] px-1 py-0 h-4">
                  {searchValue ? '1' : selectedVendors.length}
                </Badge>
              )}
              <Filter className={cn("w-3 h-3 ml-1", hasFilter ? "text-primary" : "text-muted-foreground/50")} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            <div className="p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium">Filter by Description</span>
                <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={handleClear}>
                  Clear
                </Button>
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-[10px] text-muted-foreground">Search text</Label>
                <Input
                  value={localSearch}
                  onChange={(e) => setLocalSearch(e.target.value)}
                  placeholder="Search vendor or keyword..."
                  className="h-8 text-xs"
                  data-testid="description-search-input"
                />
              </div>
              
              {topVendors.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-[10px] text-muted-foreground">Quick picks (top vendors)</Label>
                  <ScrollArea className="h-[120px]">
                    <div className="space-y-1">
                      {topVendors.map((v) => (
                        <label
                          key={v.vendor}
                          className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer"
                        >
                          <Checkbox
                            checked={localVendors.includes(v.vendor)}
                            onCheckedChange={() => toggleVendor(v.vendor)}
                          />
                          <span className="text-xs flex-1 truncate">{v.vendor}</span>
                          <span className="text-[10px] text-muted-foreground">{v.count}</span>
                        </label>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
              
              <Button size="sm" className="w-full h-7 text-xs" onClick={handleApply} data-testid="description-filter-apply">
                Apply
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        <Button
          variant="ghost"
          size="sm"
          className="h-auto p-0 hover:bg-transparent"
          onClick={() => onSort('name')}
        >
          {isActive ? (
            currentDir === 'asc' 
              ? <ChevronUp className="w-3 h-3 text-primary" />
              : <ChevronDown className="w-3 h-3 text-primary" />
          ) : (
            <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
          )}
        </Button>
      </div>
    </TableHead>
  );
};

// Category Filter Header Component (Chart of Accounts categories)
const CategoryFilterHeader = ({ 
  accounts,
  selected, 
  onApply, 
  currentSort, 
  currentDir, 
  onSort,
  open,
  onOpenChange 
}) => {
  const [localSelected, setLocalSelected] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const isActive = currentSort === 'account';
  const hasFilter = selected && selected !== 'all' && selected !== 'uncategorized';
  const isUncategorized = selected === 'uncategorized';
  
  // Group accounts by type for better organization
  const groupedAccounts = useMemo(() => {
    const groups = {
      expense: [],
      income: [],
      asset: [],
      liability: [],
      equity: []
    };
    
    accounts.forEach(acc => {
      const type = acc.type?.toLowerCase() || 'other';
      if (groups[type]) {
        groups[type].push(acc);
      }
    });
    
    return groups;
  }, [accounts]);
  
  // Filter accounts by search term
  const filteredAccounts = useMemo(() => {
    if (!searchTerm) return accounts;
    const term = searchTerm.toLowerCase();
    return accounts.filter(acc => 
      acc.name?.toLowerCase().includes(term) ||
      acc.code?.toLowerCase().includes(term)
    );
  }, [accounts, searchTerm]);
  
  // Initialize local state when popover opens
  const handleOpenChange = (isOpen) => {
    if (isOpen) {
      setLocalSelected(selected || 'all');
      setSearchTerm('');
    }
    onOpenChange(isOpen);
  };
  
  const handleApply = () => {
    onApply(localSelected);
    onOpenChange(false);
  };
  
  const handleClear = () => {
    setLocalSelected('all');
    onApply('all');
    onOpenChange(false);
  };
  
  // Get selected account name for display
  const selectedAccount = accounts.find(a => a.account_id === selected);
  const displayLabel = isUncategorized ? 'Uncat.' : (selectedAccount?.name?.slice(0, 6) || '');
  
  return (
    <TableHead className="w-28">
      <div className="flex items-center gap-1">
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-auto p-0 hover:bg-transparent font-medium text-xs",
                (hasFilter || isUncategorized) && "text-primary"
              )}
              title="Filter transactions by category"
              data-testid="category-filter-btn"
            >
              <span>Category</span>
              {(hasFilter || isUncategorized) && (
                <Badge variant="secondary" className="ml-1 text-[9px] px-1 py-0 h-4 max-w-16 truncate">
                  {displayLabel}
                </Badge>
              )}
              <Filter className={cn("w-3 h-3 ml-1", (hasFilter || isUncategorized) ? "text-primary" : "text-muted-foreground/50")} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            <div className="p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium">Filter by Category</span>
                {(hasFilter || isUncategorized) && (
                  <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={handleClear}>
                    Clear
                  </Button>
                )}
              </div>
              
              {/* Search input */}
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search categories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full h-8 pl-7 pr-3 text-xs rounded border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                  data-testid="category-search-input"
                />
              </div>
              
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {/* All Categories option */}
                <label
                  className={cn(
                    "flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50 transition-colors",
                    localSelected === 'all' && "bg-primary/10"
                  )}
                  onClick={() => setLocalSelected('all')}
                >
                  <div className={cn(
                    "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                    localSelected === 'all' ? "border-primary bg-primary" : "border-muted-foreground/30"
                  )}>
                    {localSelected === 'all' && <Check className="w-2.5 h-2.5 text-white" />}
                  </div>
                  <span className="text-xs font-medium">All Categories</span>
                </label>
                
                {/* Uncategorized option */}
                <label
                  className={cn(
                    "flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50 transition-colors border-l-2 border-amber-500",
                    localSelected === 'uncategorized' && "bg-amber-50 dark:bg-amber-900/20"
                  )}
                  onClick={() => setLocalSelected('uncategorized')}
                >
                  <div className={cn(
                    "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                    localSelected === 'uncategorized' ? "border-amber-500 bg-amber-500" : "border-muted-foreground/30"
                  )}>
                    {localSelected === 'uncategorized' && <Check className="w-2.5 h-2.5 text-white" />}
                  </div>
                  <span className="text-xs font-medium text-amber-700 dark:text-amber-400">Uncategorized</span>
                </label>
                
                {/* Grouped categories */}
                {searchTerm ? (
                  // Show flat list when searching
                  filteredAccounts.map((account) => {
                    const isSelected = localSelected === account.account_id;
                    return (
                      <label
                        key={account.account_id}
                        className={cn(
                          "flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50 transition-colors",
                          isSelected && "bg-primary/10"
                        )}
                        onClick={() => setLocalSelected(account.account_id)}
                      >
                        <div className={cn(
                          "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                          isSelected ? "border-primary bg-primary" : "border-muted-foreground/30"
                        )}>
                          {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-medium block truncate">{account.code} - {account.name}</span>
                          <span className="text-[10px] text-muted-foreground capitalize">{account.type}</span>
                        </div>
                      </label>
                    );
                  })
                ) : (
                  // Show grouped list when not searching
                  <>
                    {/* Expenses */}
                    {groupedAccounts.expense.length > 0 && (
                      <>
                        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide px-2 pt-2">Expenses</div>
                        {groupedAccounts.expense.slice(0, 10).map((account) => {
                          const isSelected = localSelected === account.account_id;
                          return (
                            <label
                              key={account.account_id}
                              className={cn(
                                "flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50 transition-colors",
                                isSelected && "bg-primary/10"
                              )}
                              onClick={() => setLocalSelected(account.account_id)}
                            >
                              <div className={cn(
                                "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                                isSelected ? "border-primary bg-primary" : "border-muted-foreground/30"
                              )}>
                                {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                              </div>
                              <span className="text-xs truncate">{account.code} - {account.name}</span>
                            </label>
                          );
                        })}
                      </>
                    )}
                    
                    {/* Income */}
                    {groupedAccounts.income.length > 0 && (
                      <>
                        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide px-2 pt-2">Income</div>
                        {groupedAccounts.income.slice(0, 10).map((account) => {
                          const isSelected = localSelected === account.account_id;
                          return (
                            <label
                              key={account.account_id}
                              className={cn(
                                "flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50 transition-colors",
                                isSelected && "bg-primary/10"
                              )}
                              onClick={() => setLocalSelected(account.account_id)}
                            >
                              <div className={cn(
                                "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                                isSelected ? "border-primary bg-primary" : "border-muted-foreground/30"
                              )}>
                                {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                              </div>
                              <span className="text-xs truncate">{account.code} - {account.name}</span>
                            </label>
                          );
                        })}
                      </>
                    )}
                    
                    {/* Assets */}
                    {groupedAccounts.asset.length > 0 && (
                      <>
                        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide px-2 pt-2">Assets</div>
                        {groupedAccounts.asset.slice(0, 5).map((account) => {
                          const isSelected = localSelected === account.account_id;
                          return (
                            <label
                              key={account.account_id}
                              className={cn(
                                "flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50 transition-colors",
                                isSelected && "bg-primary/10"
                              )}
                              onClick={() => setLocalSelected(account.account_id)}
                            >
                              <div className={cn(
                                "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                                isSelected ? "border-primary bg-primary" : "border-muted-foreground/30"
                              )}>
                                {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                              </div>
                              <span className="text-xs truncate">{account.code} - {account.name}</span>
                            </label>
                          );
                        })}
                      </>
                    )}
                  </>
                )}
              </div>
              
              <Button size="sm" className="w-full h-7 text-xs" onClick={handleApply} data-testid="category-filter-apply">
                Apply
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        <Button
          variant="ghost"
          size="sm"
          className="h-auto p-0 hover:bg-transparent"
          onClick={() => onSort('account')}
        >
          {isActive ? (
            currentDir === 'asc' 
              ? <ChevronUp className="w-3 h-3 text-primary" />
              : <ChevronDown className="w-3 h-3 text-primary" />
          ) : (
            <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
          )}
        </Button>
      </div>
    </TableHead>
  );
};

export default function Banking() {
  const navigate = useNavigate();
  const { currentWorkspace, userRole } = useWorkspace();
  const isAdmin = ['owner', 'admin'].includes((userRole || '').toLowerCase());
  const [refreshingLogos, setRefreshingLogos] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Account carousel state
  const [showArchivedAccounts, setShowArchivedAccounts] = useState(false);
  // Server-reported count of archived accounts. We keep this separate
  // from `bankAccounts` so the "Show archived (N)" toggle stays
  // discoverable even when the default list filters archived rows out.
  const [archivedAccountCount, setArchivedAccountCount] = useState(0);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState(searchParams.get('bank') || 'all');
  const [accountsLoading, setAccountsLoading] = useState(true);
  
  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [tabCounts, setTabCounts] = useState({ pending: 0, posted: 0, excluded: 0, forReview: 0, categorized: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'pending');
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filter state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Date filters default to month-to-date so the Banking page opens
  // showing the user's current period instead of an empty/all-time
  // view — keeps the list responsive and matches the dashboard's MTD
  // default. User can clear or change them.
  const [dateFrom, setDateFrom] = useState(() => startOfMonth(new Date()));
  const [dateTo, setDateTo] = useState(() => new Date());
  const [txnType, setTxnType] = useState('all');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  
  // Resizable filter panel state
  const [filterPanelWidth, setFilterPanelWidth] = useState(() => {
    const saved = localStorage.getItem('banking_filter_panel_width');
    return saved ? parseInt(saved, 10) : 280;
  });
  const filterPanelRef = useRef(null);
  const isDragging = useRef(false);
  
  // Header filter state
  const [statusFilter, setStatusFilter] = useState([]); // Array of selected statuses
  const [descriptionFilter, setDescriptionFilter] = useState(''); // Text search for description
  const [topVendors, setTopVendors] = useState([]); // Quick picks for description filter
  const [selectedVendors, setSelectedVendors] = useState([]); // Selected vendor quick picks
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);
  const [descriptionPopoverOpen, setDescriptionPopoverOpen] = useState(false);
  // Honor a `?filter=uncategorized` deep-link from the Dashboard so users
  // can jump straight from the "$0 revenue" empty state into the cause.
  const [categoryFilter, setCategoryFilter] = useState(
    () => (searchParams.get('filter') === 'uncategorized' ? 'uncategorized' : 'all'),
  );
  const [categoryPopoverOpen, setCategoryPopoverOpen] = useState(false);
  
  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Expanded row state
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // AI Buck banner state
  const [aiBuckCount, setAiBuckCount] = useState(0);
  // Bulk "Run AI Buck on all uncategorized" state — drives the loading
  // spinner on the action button and gates double-clicks.
  const [bulkAiBuckRunning, setBulkAiBuckRunning] = useState(false);
  
  // Drawer/Dialog state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  
  // Note state
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  
  // Bulk action state
  const [bulkActionDialog, setBulkActionDialog] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkAccountId, setBulkAccountId] = useState('');
  
  // Bulk Post Categorized state
  const [bulkPostPreview, setBulkPostPreview] = useState(null);
  const [bulkPostLoading, setBulkPostLoading] = useState(false);
  
  // Quick Categorize state
  const [quickCategorizePreview, setQuickCategorizePreview] = useState(null);
  const [quickCategorizeLoading, setQuickCategorizeLoading] = useState(false);
  const [quickCategorizeAccountId, setQuickCategorizeAccountId] = useState('');
  const [quickCategorizeContactId, setQuickCategorizeContactId] = useState('');
  const [quickCategorizeMemo, setQuickCategorizeMemo] = useState('');

  // "Apply Similar" follow-up prompt — surfaced after Quick Categorize /
  // Bulk Post / inline categorization finds OTHER uncategorized transactions
  // from the same merchant. Lets the user mass-apply the same category with
  // a single click instead of repeating themselves.
  const [similarPrompt, setSimilarPrompt] = useState(null);
  // similarPrompt shape:
  //   { merchant, items: [...], accountId, accountName, contactId, memo, mode }
  //   mode: 'categorize' (apply category + leave for_review) or 'post' (categorize then bulk-post)
  const [similarProcessing, setSimilarProcessing] = useState(false);
  
  // Entry creation state
  const [accounts, setAccounts] = useState([]);
  const [creatingEntry, setCreatingEntry] = useState(false);
  const [entryForm, setEntryForm] = useState({
    transaction_type: 'spend',
    account_id: '',
    memo: '',
    customer_vendor: '',
  });

  // Split transaction state
  const [splitDrawerOpen, setSplitDrawerOpen] = useState(false);
  const [splitTransaction, setSplitTransaction] = useState(null);
  const [splitLines, setSplitLines] = useState([]);
  const [splitLoading, setSplitLoading] = useState(false);
  const [splitSaving, setSplitSaving] = useState(false);
  const [splitPosting, setSplitPosting] = useState(false);
  const [existingSplit, setExistingSplit] = useState(null);
  
  // Contacts state for vendor/customer selection
  const [contacts, setContacts] = useState([]);

  // Inline categorization state (for expanded row)
  const [inlineCategoryId, setInlineCategoryId] = useState('');
  const [inlinePosting, setInlinePosting] = useState(false);

  // Add Account drawer state
  const [addAccountDrawerOpen, setAddAccountDrawerOpen] = useState(false);
  const [addAccountStep, setAddAccountStep] = useState('choose'); // 'choose', 'manual'
  const [manualAccountForm, setManualAccountForm] = useState({
    account_name: '',
    account_type: 'checking',
    currency: 'USD',
    opening_balance: '',
    opening_balance_date: format(new Date(), 'yyyy-MM-dd'),
    institution_name: '',
    mask: '',
    gl_account_id: '',
    create_gl_account: true
  });
  const [creatingManualAccount, setCreatingManualAccount] = useState(false);

  // Manual Transaction drawer state
  const [manualTxnDrawerOpen, setManualTxnDrawerOpen] = useState(false);
  const [manualTxnForm, setManualTxnForm] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    description: '',
    amount: '',
    transaction_type: 'spend', // 'spend' or 'receive'
    merchant_name: '',
    memo: '',
    category_account_id: '', // Optional category account
    contact_id: '' // Optional vendor/customer
  });
  const [creatingManualTxn, setCreatingManualTxn] = useState(false);

  // Document Scanner dialog state
  const [scannerDialogOpen, setScannerDialogOpen] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Filter panel resize handlers
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging.current || !filterPanelRef.current) return;
      
      const panelRect = filterPanelRef.current.getBoundingClientRect();
      const newWidth = e.clientX - panelRect.left;
      
      // Constrain width between 200 and 500
      const constrainedWidth = Math.max(200, Math.min(500, newWidth));
      setFilterPanelWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        // Persist to localStorage
        localStorage.setItem('banking_filter_panel_width', filterPanelWidth.toString());
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [filterPanelWidth]);

  // Fetch bank accounts (unified - both Plaid and Manual)
  const fetchAccounts = useCallback(async () => {
    if (!currentWorkspace) return;
    
    setAccountsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/accounts?workspace_id=${currentWorkspace.workspace_id}${showArchivedAccounts ? '&include_archived=true' : ''}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setBankAccounts(data.accounts || data || []);
        // Backend now returns `archived_count` alongside the rows so
        // the "Show archived" toggle stays discoverable even when
        // the default list hides the archived ones.
        if (typeof data?.archived_count === 'number') {
          setArchivedAccountCount(data.archived_count);
        }
      }
    } catch (error) {
      console.error('Failed to fetch bank accounts:', error);
    } finally {
      setAccountsLoading(false);
    }
  }, [currentWorkspace, showArchivedAccounts]);

  // Manual "Sync now" on a single bank — uses Plaid's FREE transactions_sync
  // (no per-call charge). Returns the count of new txns pulled so we can
  // show a meaningful toast instead of a generic "done".
  const [syncingAll, setSyncingAll] = useState(false);
  const handleSyncAllBanks = async () => {
    if (!currentWorkspace || syncingAll) return;
    setSyncingAll(true);
    const toastId = toast.loading('Syncing every connected bank…');
    try {
      const r = await fetch(
        `${API_URL}/api/banking/accounts/sync-all?workspace_id=${currentWorkspace.workspace_id}`,
        { method: 'POST' }
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Sync-all failed');
      }
      const data = await r.json();
      const failed = data.failed_items ? ` · ${data.failed_items} failed` : '';
      toast.success(
        `Synced ${data.synced_items} bank${data.synced_items === 1 ? '' : 's'} · ${data.total_new_transactions} new transactions${failed}`,
        { id: toastId },
      );
      fetchAccounts();
      fetchTransactions();
    } catch (e) {
      toast.error(e.message || 'Sync-all failed', { id: toastId });
    } finally {
      setSyncingAll(false);
    }
  };

  const handleSyncBankNow = async (account) => {
    if (!currentWorkspace) return;
    const toastId = toast.loading(`Syncing ${account.institution_name || 'account'}…`);
    try {
      const r = await fetch(
        `${API_URL}/api/banking/accounts/${account.bank_id}/sync?workspace_id=${currentWorkspace.workspace_id}`,
        { method: 'POST' }
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Sync failed');
      }
      const data = await r.json();
      const msg = data.new_transactions > 0
        ? `Synced ${data.new_transactions} new transaction${data.new_transactions === 1 ? '' : 's'}`
        : 'Up to date — no new transactions';
      toast.success(msg, { id: toastId });
      fetchAccounts();
      fetchTransactions();
    } catch (e) {
      toast.error(e.message || 'Sync failed', { id: toastId });
    }
  };


  // Archive / unarchive a bank account from the carousel kebab. Optimistic
  // toggle then revalidate so the user sees the card flip immediately.
  const handleArchiveBankAccount = async (account) => {
    if (!currentWorkspace) return;
    const next = !account.is_archived;
    const verb = next ? 'Archive' : 'Restore';
    if (next && !window.confirm(
      `Archive ${account.institution_name || 'this account'}? ` +
      `It will be hidden from the banking page, dashboard cash total, and reports drop-downs. ` +
      `Transaction history is preserved and you can restore it any time.`
    )) {
      return;
    }
    try {
      const r = await fetch(
        `${API_URL}/api/banking/accounts/${account.bank_id}/archive?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_archived: next }),
        }
      );
      if (!r.ok) throw new Error(await r.text());
      toast.success(`${verb}d ${account.institution_name || 'account'}`);
      // If we just archived the currently-selected account, fall back to All.
      if (next && selectedBankId === account.bank_id) setSelectedBankId('all');
      fetchAccounts();
    } catch (e) {
      toast.error(`Could not ${verb.toLowerCase()} account`);
    }
  };

  // Admin-only: backfill institution name + logo + color for any
  // already-linked banks that pre-date the institution-resolver
  // (shipped Feb 2026). Safe to call repeatedly.
  const handleRefreshLogos = useCallback(async () => {
    if (!currentWorkspace) return;
    setRefreshingLogos(true);
    try {
      const r = await fetch(
        `${API_URL}/api/plaid/refresh-institutions?workspace_id=${currentWorkspace.workspace_id}`,
        { method: 'POST' },
      );
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(
          `Refreshed ${data.repaired || 0} bank ${data.repaired === 1 ? 'logo' : 'logos'}.`,
        );
        fetchAccounts();
      } else {
        toast.error(data.detail || 'Failed to refresh logos');
      }
    } catch (e) {
      toast.error('Failed to refresh logos');
    } finally {
      setRefreshingLogos(false);
    }
  }, [currentWorkspace, fetchAccounts]);

  // Fetch AI Buck count for banner
  const fetchAiBuckCount = useCallback(async () => {
    if (!currentWorkspace) return;
    
    try {
      const params = new URLSearchParams({
        workspace_id: currentWorkspace.workspace_id
      });
      if (selectedBankId && selectedBankId !== 'all') {
        params.append('bank_id', selectedBankId);
      }
      if (dateFrom) {
        params.append('date_from', format(dateFrom, 'yyyy-MM-dd'));
      }
      if (dateTo) {
        params.append('date_to', format(dateTo, 'yyyy-MM-dd'));
      }
      
      const response = await fetch(
        `${API_URL}/api/banking/ai-buck-count?${params}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setAiBuckCount(data.count);
      }
    } catch (error) {
      console.error('Failed to fetch AI Buck count:', error);
    }
  }, [currentWorkspace, selectedBankId, dateFrom, dateTo]);

  // Fetch transactions with filters
  const fetchTransactions = useCallback(async () => {
    if (!currentWorkspace) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        workspace_id: currentWorkspace.workspace_id,
        tab: activeTab,
        sort_by: sortBy,
        sort_dir: sortDir,
        page: page.toString(),
        page_size: pageSize.toString()
      });
      
      if (selectedBankId && selectedBankId !== 'all') {
        params.append('bank_id', selectedBankId);
      }
      // Use descriptionFilter for search if set, otherwise use general search
      const searchTerm = descriptionFilter || debouncedSearch;
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      // Vendor quick picks filter - exact match on vendor names
      if (selectedVendors.length > 0) {
        params.append('vendor_in', selectedVendors.join(','));
      }
      if (dateFrom) {
        params.append('date_from', format(dateFrom, 'yyyy-MM-dd'));
      }
      if (dateTo) {
        params.append('date_to', format(dateTo, 'yyyy-MM-dd'));
      }
      if (txnType && txnType !== 'all') {
        params.append('txn_type', txnType);
      }
      if (amountMin) {
        params.append('amount_min', amountMin);
      }
      if (amountMax) {
        params.append('amount_max', amountMax);
      }
      // Status header filter - comma-separated list
      if (statusFilter.length > 0) {
        params.append('status_in', statusFilter.join(','));
      }
      
      const response = await fetch(
        `${API_URL}/api/banking/transactions-enhanced?${params}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions);
        setTotalCount(data.total);
        setTotalPages(data.total_pages || 1);
        setTabCounts(data.tab_counts || { pending: 0, posted: 0, excluded: 0 });
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace, activeTab, selectedBankId, debouncedSearch, descriptionFilter, selectedVendors, dateFrom, dateTo, txnType, amountMin, amountMax, sortBy, sortDir, page, pageSize, statusFilter]);

  // Fetch top vendors for quick picks
  const fetchTopVendors = useCallback(async () => {
    if (!currentWorkspace) return;
    
    try {
      const params = new URLSearchParams({
        workspace_id: currentWorkspace.workspace_id,
        tab: activeTab,
        limit: '10'
      });
      
      if (selectedBankId && selectedBankId !== 'all') {
        params.append('bank_id', selectedBankId);
      }
      if (dateFrom) {
        params.append('date_from', format(dateFrom, 'yyyy-MM-dd'));
      }
      if (dateTo) {
        params.append('date_to', format(dateTo, 'yyyy-MM-dd'));
      }
      
      const response = await fetch(
        `${API_URL}/api/banking/transactions/top-vendors?${params}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setTopVendors(data.vendors || []);
      }
    } catch (error) {
      console.error('Failed to fetch top vendors:', error);
    }
  }, [currentWorkspace, activeTab, selectedBankId, dateFrom, dateTo]);

  // Fetch chart of accounts
  const fetchChartAccounts = useCallback(async () => {
    if (!currentWorkspace) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/accounts`
      );
      
      if (response.ok) {
        const data = await response.json();
        setAccounts(data);
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }
  }, [currentWorkspace]);

  // Fetch expanded row detail
  const fetchTransactionDetail = useCallback(async (transactionId) => {
    if (!currentWorkspace) return;
    
    setLoadingDetail(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/${transactionId}/detail?workspace_id=${currentWorkspace.workspace_id}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setExpandedDetail(data);
      }
    } catch (error) {
      console.error('Failed to fetch transaction detail:', error);
    } finally {
      setLoadingDetail(false);
    }
  }, [currentWorkspace]);

  // Fetch contacts for vendor/customer selection
  const fetchContacts = useCallback(async () => {
    if (!currentWorkspace) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/contacts`
      );
      
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    fetchAccounts();
    fetchChartAccounts();
    fetchContacts();
  }, [fetchAccounts, fetchChartAccounts, fetchContacts]);

  // ─── Silent auto-heal of bank logos on mount ────────────────────
  // After load, if any Plaid-linked account still has no
  // `institution_logo_url`, fire `/refresh-institutions` ONCE per
  // workspace+session and refetch. The user no longer has to know about
  // the "Refresh logos" button — keeping that for explicit re-runs.
  // (Jun 16, 2026 — fix for the production Chase-logos issue.)
  useEffect(() => {
    if (!currentWorkspace) return;
    if (!bankAccounts || bankAccounts.length === 0) return;
    const needsHeal = bankAccounts.some(
      (a) => a.source === 'plaid' &&
        (!a.institution_logo_url ||
         (a.institution_name || '').toLowerCase() === 'unknown bank'),
    );
    if (!needsHeal) return;
    const sessKey = `bank_logos_healed:${currentWorkspace.workspace_id}`;
    if (window.sessionStorage.getItem(sessKey)) return;
    window.sessionStorage.setItem(sessKey, '1');
    (async () => {
      try {
        await fetch(
          `${API_URL}/api/plaid/refresh-institutions?workspace_id=${currentWorkspace.workspace_id}`,
          { method: 'POST' },
        );
        fetchAccounts();
      } catch { /* best-effort, silent */ }
    })();
  }, [currentWorkspace, bankAccounts, fetchAccounts]);

  useEffect(() => {
    fetchTransactions();
    fetchAiBuckCount();
    setSelectedIds(new Set());
    setSelectAll(false);
    setExpandedRow(null);
    setExpandedDetail(null);
  }, [fetchTransactions, fetchAiBuckCount]);

  // Fetch top vendors when description popover opens
  useEffect(() => {
    if (descriptionPopoverOpen) {
      fetchTopVendors();
    }
  }, [descriptionPopoverOpen, fetchTopVendors]);

  // Reset page when filters change (including header filters)
  useEffect(() => {
    setPage(1);
  }, [activeTab, selectedBankId, debouncedSearch, descriptionFilter, selectedVendors, dateFrom, dateTo, txnType, amountMin, amountMax, statusFilter]);

  // Update URL params (include header filters)
  useEffect(() => {
    const params = new URLSearchParams();
    if (activeTab !== 'pending') params.set('tab', activeTab);
    if (selectedBankId !== 'all') params.set('bank', selectedBankId);
    if (statusFilter.length > 0) params.set('status_in', statusFilter.join(','));
    if (descriptionFilter) params.set('q', descriptionFilter);
    if (selectedVendors.length > 0) params.set('vendor_in', selectedVendors.join(','));
    setSearchParams(params, { replace: true });
  }, [activeTab, selectedBankId, statusFilter, descriptionFilter, selectedVendors, setSearchParams]);

  // Initialize header filters from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const statusInParam = params.get('status_in');
    if (statusInParam) {
      setStatusFilter(statusInParam.split(',').filter(s => s));
    }
    const qParam = params.get('q');
    if (qParam) {
      setDescriptionFilter(qParam);
    }
    const vendorInParam = params.get('vendor_in');
    if (vendorInParam) {
      setSelectedVendors(vendorInParam.split(',').filter(v => v));
    }
  }, []); // Only on mount - intentionally no dependencies

  // Handle sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  // Clear header filters only (Status and Description)
  const clearHeaderFilters = () => {
    setStatusFilter([]);
    setDescriptionFilter('');
    setSelectedVendors([]);
    setCategoryFilter('all');
  };

  // Check if any header filter is active
  const hasHeaderFilters = statusFilter.length > 0 || descriptionFilter || selectedVendors.length > 0 || (categoryFilter && categoryFilter !== 'all');

  // Apply client-side category filter to transactions
  const filteredByCategory = useMemo(() => {
    if (!categoryFilter || categoryFilter === 'all') {
      return transactions;
    }
    if (categoryFilter === 'uncategorized') {
      // Show transactions without a category assigned
      return transactions.filter(t => !t.category_account_id && !t.suggested_account_id && t.status !== 'posted');
    }
    // Filter by specific category account_id
    return transactions.filter(t => 
      t.category_account_id === categoryFilter || 
      t.suggested_account_id === categoryFilter
    );
  }, [transactions, categoryFilter]);

  // Uncategorized transactions in the current view that don't already
  // have a pending AI Buck suggestion. These are the ones the
  // "Auto-suggest all" batch action targets.
  const uncategorizedTxnIds = useMemo(() => (
    transactions
      .filter(t => (
        !t.category_account_id &&
        !t.suggested_account_id &&
        !t.ai_buck_suggestion_id &&
        t.status !== 'posted' &&
        t.status !== 'excluded'
      ))
      .map(t => t.transaction_id)
  ), [transactions]);

  // Handle selection
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
      setSelectAll(false);
    } else {
      setSelectedIds(new Set(transactions.map(t => t.transaction_id)));
      setSelectAll(true);
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
    setSelectAll(newSelected.size === transactions.length && transactions.length > 0);
  };

  // Handle row expansion
  const toggleRow = async (id) => {
    if (expandedRow === id) {
      setExpandedRow(null);
      setExpandedDetail(null);
    } else {
      setExpandedRow(id);
      await fetchTransactionDetail(id);
    }
  };

  // Handle single transaction actions
  const handleOpenCreateEntry = (txn) => {
    setSelectedTransaction(txn);
    setEntryForm({
      transaction_type: txn.amount > 0 ? 'spend' : 'receive',
      account_id: txn.suggested_account_id || '',
      memo: txn.merchant_name || txn.name,
      customer_vendor: '',
    });
    setDrawerOpen(true);
  };

  const handleFindMatch = (txn) => {
    navigate(`/banking/transactions/${txn.transaction_id}/match`);
  };

  const handleExclude = async (txn) => {
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/${txn.transaction_id}/exclude?workspace_id=${currentWorkspace.workspace_id}`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        toast.success('Transaction excluded');
        fetchTransactions();
        fetchAccounts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to exclude');
      }
    } catch (error) {
      toast.error('Failed to exclude transaction');
    }
  };

  const handleRestore = async (txn) => {
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/${txn.transaction_id}/restore?workspace_id=${currentWorkspace.workspace_id}`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        toast.success('Transaction restored');
        fetchTransactions();
        fetchAccounts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to restore');
      }
    } catch (error) {
      toast.error('Failed to restore transaction');
    }
  };

  const handleCreateEntry = async () => {
    if (!selectedTransaction || !entryForm.account_id) return;
    
    setCreatingEntry(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/${selectedTransaction.transaction_id}/create-entry?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            transaction_type: entryForm.transaction_type,
            account_id: entryForm.account_id,
            memo: entryForm.memo,
            customer_vendor: entryForm.customer_vendor
          })
        }
      );
      
      if (response.ok) {
        toast.success('Journal entry created and posted');
        setDrawerOpen(false);
        fetchTransactions();
        fetchAccounts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create entry');
      }
    } catch (error) {
      toast.error('Failed to create entry');
    } finally {
      setCreatingEntry(false);
    }
  };

  // Inline posting from expanded row
  const handleInlinePost = async (txn, accountId) => {
    if (!accountId) {
      toast.error('Please select a category account');
      return;
    }
    
    // Validate bank account is present
    if (!txn.bank_id) {
      toast.error('Transaction has no bank account linked. Cannot post.');
      return;
    }
    
    setInlinePosting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/${txn.transaction_id}/create-entry?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            transaction_type: txn.amount > 0 ? 'spend' : 'receive',
            account_id: accountId,
            memo: txn.merchant_name || txn.name
          })
        }
      );
      
      if (response.ok) {
        toast.success('Transaction posted successfully');
        setExpandedRow(null);
        setExpandedDetail(null);
        setInlineCategoryId('');
        fetchTransactions();
        fetchAccounts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to post transaction');
      }
    } catch (error) {
      toast.error('Failed to post transaction');
    } finally {
      setInlinePosting(false);
    }
  };

  // When expanded row changes, initialize inline category from suggestion
  useEffect(() => {
    if (expandedDetail && expandedDetail.suggested_account_id) {
      setInlineCategoryId(expandedDetail.suggested_account_id);
    } else {
      setInlineCategoryId('');
    }
  }, [expandedDetail]);

  // ── Quick-post / inline edits — keep the bookkeeper in flow ─────────────
  //
  // `handleQuickPost`: clicked from the row's green "+" button. If the
  //   transaction has a suggested_account_id (set by AI Buck or via the
  //   inline category dropdown on the row), post immediately without opening
  //   the modal. Falls back to opening the categorize panel if nothing is set.
  // `handleRowCategoryChange`: persists the user's pick into the txn doc so
  //   subsequent renders + the quick-post button see the chosen account.
  // `handleRecategorizePosted`: replaces the old void/reverse JE flow with a
  //   user-friendly "change category" action that hides journal-entry jargon.
  // `handleCreateRuleFromTxn`: one-click rule creation from a row's merchant
  //   (or description) — saves users a trip to AI Buck settings.

  const [quickPostingId, setQuickPostingId] = useState(null);

  const handleRowCategoryChange = async (txn, newAccountId) => {
    if (!newAccountId || newAccountId === txn.suggested_account_id) return;
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/${txn.transaction_id}/categorize?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ account_id: newAccountId }),
        },
      );
      if (!response.ok) {
        const e = await response.json().catch(() => ({}));
        throw new Error(e.detail || 'Failed to update category');
      }
      toast.success('Category updated');
      fetchTransactions();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleQuickPost = async (txn) => {
    if (!txn.suggested_account_id) {
      // No category set on the row yet — fall back to the existing modal so
      // the user gets the full transaction-type / memo picker.
      handleOpenCreateEntry(txn);
      return;
    }
    setQuickPostingId(txn.transaction_id);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/${txn.transaction_id}/create-entry?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            transaction_type: txn.amount > 0 ? 'spend' : 'receive',
            account_id: txn.suggested_account_id,
            memo: txn.merchant_name || txn.name,
          }),
        },
      );
      if (response.ok) {
        toast.success(`Posted ${txn.merchant_name || txn.name}`);
        fetchTransactions();
        fetchAccounts();
      } else {
        const e = await response.json();
        toast.error(e.detail || 'Failed to post');
      }
    } finally {
      setQuickPostingId(null);
    }
  };

  const handleRecategorizePosted = async (txn, newAccountId) => {
    if (!newAccountId || newAccountId === txn.suggested_account_id) return;
    setQuickPostingId(txn.transaction_id);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/${txn.transaction_id}/recategorize?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ account_id: newAccountId }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success(data.message || 'Category updated');
        fetchTransactions();
        fetchAccounts();
      } else {
        toast.error(data.detail || 'Failed to update category');
      }
    } finally {
      setQuickPostingId(null);
    }
  };

  const handleCreateRuleFromTxn = async (txn, matchField = 'merchant') => {
    if (!txn.suggested_account_id) {
      toast.error('Pick a category on this row first — that becomes the rule target.');
      return;
    }
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/${txn.transaction_id}/create-rule?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            target_account_id: txn.suggested_account_id,
            match_field: matchField,
          }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success(`Rule created: ${data.name}. Future similar ${matchField === 'merchant' ? 'merchants' : 'descriptions'} will auto-categorize.`);
      } else {
        toast.error(data.detail || 'Failed to create rule');
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Split transaction handlers
  const openSplitDrawer = async (txn) => {
    setSplitTransaction(txn);
    setSplitDrawerOpen(true);
    setSplitLoading(true);
    setExistingSplit(null);
    
    // Check if there's an existing split
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/${txn.transaction_id}/split?workspace_id=${currentWorkspace.workspace_id}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setExistingSplit(data);
        // Convert existing lines to edit format
        setSplitLines(data.lines.map(line => ({
          id: line.line_id,
          account_id: line.account_id,
          amount: line.amount.toString(),
          memo: line.memo || '',
          contact_id: line.contact_id || ''
        })));
      } else if (response.status === 404) {
        // No existing split, start fresh
        const absAmount = Math.abs(txn.amount);
        setSplitLines([
          { id: crypto.randomUUID(), account_id: '', amount: '', memo: '', contact_id: '' },
          { id: crypto.randomUUID(), account_id: '', amount: '', memo: '', contact_id: '' }
        ]);
      }
    } catch (error) {
      console.error('Failed to fetch split:', error);
      const absAmount = Math.abs(txn.amount);
      setSplitLines([
        { id: crypto.randomUUID(), account_id: '', amount: '', memo: '', contact_id: '' },
        { id: crypto.randomUUID(), account_id: '', amount: '', memo: '', contact_id: '' }
      ]);
    } finally {
      setSplitLoading(false);
    }
  };

  const closeSplitDrawer = () => {
    setSplitDrawerOpen(false);
    setSplitTransaction(null);
    setSplitLines([]);
    setExistingSplit(null);
  };

  const addSplitLine = () => {
    setSplitLines([...splitLines, { 
      id: crypto.randomUUID(), 
      account_id: '', 
      amount: '', 
      memo: '', 
      contact_id: '' 
    }]);
  };

  const removeSplitLine = (id) => {
    if (splitLines.length <= 2) {
      toast.error('Split requires at least 2 lines');
      return;
    }
    setSplitLines(splitLines.filter(line => line.id !== id));
  };

  const updateSplitLine = (id, field, value) => {
    setSplitLines(splitLines.map(line => 
      line.id === id ? { ...line, [field]: value } : line
    ));
  };

  const splitTotal = useMemo(() => {
    return splitLines.reduce((sum, line) => {
      const amount = parseFloat(line.amount) || 0;
      return sum + amount;
    }, 0);
  }, [splitLines]);

  const splitRemaining = useMemo(() => {
    if (!splitTransaction) return 0;
    const originalAmount = Math.abs(splitTransaction.amount);
    return Math.round((originalAmount - splitTotal) * 100) / 100;
  }, [splitTransaction, splitTotal]);

  const isSplitValid = useMemo(() => {
    if (!splitTransaction) return false;
    // Check all lines have account and amount
    const allLinesValid = splitLines.every(line => line.account_id && parseFloat(line.amount) > 0);
    // Check remaining is exactly 0
    const remainingZero = Math.abs(splitRemaining) < 0.01;
    return allLinesValid && remainingZero && splitLines.length >= 2;
  }, [splitTransaction, splitLines, splitRemaining]);

  const saveSplit = async (andPost = false) => {
    if (!splitTransaction || !isSplitValid) return;
    
    if (andPost) {
      setSplitPosting(true);
    } else {
      setSplitSaving(true);
    }
    
    try {
      // First save the split
      const saveResponse = await fetch(
        `${API_URL}/api/banking/transactions/${splitTransaction.transaction_id}/split?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lines: splitLines.map(line => ({
              account_id: line.account_id,
              amount: parseFloat(line.amount),
              memo: line.memo || null,
              contact_id: line.contact_id || null
            }))
          })
        }
      );
      
      if (!saveResponse.ok) {
        const error = await saveResponse.json();
        toast.error(error.detail || 'Failed to save split');
        return;
      }
      
      if (andPost) {
        // Now post the split
        const postResponse = await fetch(
          `${API_URL}/api/banking/transactions/${splitTransaction.transaction_id}/split/post?workspace_id=${currentWorkspace.workspace_id}`,
          { method: 'POST' }
        );
        
        if (postResponse.ok) {
          toast.success('Split posted successfully');
          closeSplitDrawer();
          fetchTransactions();
          fetchAccounts();
        } else {
          const error = await postResponse.json();
          toast.error(error.detail || 'Failed to post split');
        }
      } else {
        toast.success('Split saved for review');
        closeSplitDrawer();
        fetchTransactions();
      }
    } catch (error) {
      toast.error('Failed to save split');
    } finally {
      setSplitSaving(false);
      setSplitPosting(false);
    }
  };

  const deleteSplit = async () => {
    if (!splitTransaction) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/${splitTransaction.transaction_id}/split?workspace_id=${currentWorkspace.workspace_id}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        toast.success('Split deleted');
        closeSplitDrawer();
        fetchTransactions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete split');
      }
    } catch (error) {
      toast.error('Failed to delete split');
    }
  };

  const postExistingSplit = async (txn) => {
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/${txn.transaction_id}/split/post?workspace_id=${currentWorkspace.workspace_id}`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        toast.success('Split posted successfully');
        fetchTransactions();
        fetchAccounts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to post split');
      }
    } catch (error) {
      toast.error('Failed to post split');
    }
  };

  // Manual Account handlers
  const openAddAccountDrawer = () => {
    setAddAccountDrawerOpen(true);
    setAddAccountStep('choose');
    setManualAccountForm({
      account_name: '',
      account_type: 'checking',
      currency: 'USD',
      opening_balance: '',
      opening_balance_date: format(new Date(), 'yyyy-MM-dd'),
      institution_name: '',
      mask: '',
      gl_account_id: '',
      create_gl_account: true
    });
  };

  const closeAddAccountDrawer = () => {
    setAddAccountDrawerOpen(false);
    setAddAccountStep('choose');
  };

  const createManualAccount = async () => {
    if (!manualAccountForm.account_name || !manualAccountForm.opening_balance) {
      toast.error('Account name and opening balance are required');
      return;
    }
    
    setCreatingManualAccount(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/manual-accounts?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            account_name: manualAccountForm.account_name,
            account_type: manualAccountForm.account_type,
            currency: manualAccountForm.currency,
            opening_balance: parseFloat(manualAccountForm.opening_balance),
            opening_balance_date: manualAccountForm.opening_balance_date,
            institution_name: manualAccountForm.institution_name || null,
            mask: manualAccountForm.mask || null,
            gl_account_id: manualAccountForm.gl_account_id || null,
            create_gl_account: manualAccountForm.create_gl_account
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Manual account created');
        closeAddAccountDrawer();
        fetchAccounts();
        // Select the new account
        setSelectedBankId(data.bank_id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create account');
      }
    } catch (error) {
      toast.error('Failed to create account');
    } finally {
      setCreatingManualAccount(false);
    }
  };

  // Manual Transaction handlers
  const openManualTxnDrawer = () => {
    setManualTxnDrawerOpen(true);
    setManualTxnForm({
      date: format(new Date(), 'yyyy-MM-dd'),
      description: '',
      amount: '',
      transaction_type: 'spend',
      merchant_name: '',
      memo: '',
      category_account_id: '',
      contact_id: ''
    });
  };

  const closeManualTxnDrawer = () => {
    setManualTxnDrawerOpen(false);
  };

  const createManualTransaction = async () => {
    if (!manualTxnForm.description || !manualTxnForm.amount) {
      toast.error('Description and amount are required');
      return;
    }
    
    // Get the selected manual account
    const selectedAccount = bankAccounts.find(a => a.bank_id === selectedBankId);
    if (!selectedAccount || selectedAccount.source !== 'manual') {
      toast.error('Please select a manual account first');
      return;
    }
    
    setCreatingManualTxn(true);
    try {
      // Amount: positive for spend (outflow), negative for receive (inflow)
      const amount = manualTxnForm.transaction_type === 'spend' 
        ? Math.abs(parseFloat(manualTxnForm.amount))
        : -Math.abs(parseFloat(manualTxnForm.amount));
      
      const response = await fetch(
        `${API_URL}/api/banking/manual-accounts/${selectedBankId}/transactions?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            date: manualTxnForm.date,
            description: manualTxnForm.description,
            amount: amount,
            merchant_name: manualTxnForm.merchant_name || null,
            memo: manualTxnForm.memo || null,
            category_account_id: manualTxnForm.category_account_id || null,
            contact_id: manualTxnForm.contact_id || null
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.category_account_id) {
          toast.success('Transaction created with category - ready for posting');
        } else if (data.ai_buck_routed) {
          toast.success('Transaction sent to AI Buck for categorization suggestions');
        } else {
          toast.success('Transaction created - available in For Review');
        }
        closeManualTxnDrawer();
        fetchTransactions();
        fetchAccounts();
        fetchAiBuckCount(); // Refresh AI Buck count
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create transaction');
      }
    } catch (error) {
      toast.error('Failed to create transaction');
    } finally {
      setCreatingManualTxn(false);
    }
  };

  // Get filtered accounts based on transaction type
  const filteredCategoryAccounts = useMemo(() => {
    if (manualTxnForm.transaction_type === 'spend') {
      return accounts.filter(a => ['expense', 'asset', 'liability'].includes(a.type));
    } else {
      return accounts.filter(a => ['income', 'asset', 'liability'].includes(a.type));
    }
  }, [accounts, manualTxnForm.transaction_type]);

  // Get filtered contacts based on transaction type
  const filteredContacts = useMemo(() => {
    if (manualTxnForm.transaction_type === 'spend') {
      return contacts.filter(c => c.type === 'vendor' || c.type === 'both');
    } else {
      return contacts.filter(c => c.type === 'customer' || c.type === 'both');
    }
  }, [contacts, manualTxnForm.transaction_type]);

  // Check if selected account is manual
  const selectedAccountIsManual = useMemo(() => {
    if (selectedBankId === 'all') return false;
    const account = bankAccounts.find(a => a.bank_id === selectedBankId);
    return account?.source === 'manual';
  }, [selectedBankId, bankAccounts]);

  // Add note to transaction
  const handleAddNote = async () => {
    if (!expandedRow || !newNote.trim()) return;
    
    setAddingNote(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/${expandedRow}/notes?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: newNote.trim() })
        }
      );
      
      if (response.ok) {
        setNewNote('');
        await fetchTransactionDetail(expandedRow);
        toast.success('Note added');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add note');
      }
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  // Bulk actions
  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0) {
      toast.error('No transactions selected');
      return;
    }
    
    if (action === 'post') {
      setBulkActionDialog('post');
      return;
    }
    
    setBulkProcessing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/bulk-action?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            transaction_ids: Array.from(selectedIds),
            action: action
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.success} transactions ${action === 'exclude' ? 'excluded' : 'restored'}`);
        setSelectedIds(new Set());
        setSelectAll(false);
        fetchTransactions();
        fetchAccounts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Bulk action failed');
      }
    } catch (error) {
      toast.error('Bulk action failed');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkPost = async () => {
    if (!bulkAccountId) {
      toast.error('Please select an account');
      return;
    }
    
    setBulkProcessing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/bulk-action?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            transaction_ids: Array.from(selectedIds),
            action: 'post',
            account_id: bulkAccountId
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.success} entries created and posted`);
        setBulkActionDialog(null);
        setBulkAccountId('');
        setSelectedIds(new Set());
        setSelectAll(false);
        fetchTransactions();
        fetchAccounts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Bulk post failed');
      }
    } catch (error) {
      toast.error('Bulk post failed');
    } finally {
      setBulkProcessing(false);
    }
  };

  // Run AI Buck across every uncategorized transaction in the current
  // view. One click triggers Claude-backed categorization for the whole
  // batch and refreshes the list when done — turns ~20 minutes of
  // one-by-one suggestion runs into a single action.
  const handleBulkRunAiBuck = async (txnIds) => {
    if (!currentWorkspace?.workspace_id) return;
    if (!txnIds || txnIds.length === 0) {
      toast.info('No uncategorized transactions to run AI Buck on');
      return;
    }
    setBulkAiBuckRunning(true);
    const toastId = toast.loading(
      `Running AI Buck on ${txnIds.length} transaction${txnIds.length === 1 ? '' : 's'}…`
    );
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/bulk-run-ai-buck?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ transaction_ids: txnIds, max: 1000 }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        toast.success(
          `AI Buck suggested categories for ${data.suggested} of ${data.processed} transactions`,
          { id: toastId }
        );
        // Pull fresh transactions + the pending suggestion banner count
        fetchTransactions();
        fetchAiBuckCount();
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to run AI Buck on uncategorized', { id: toastId });
      }
    } catch (e) {
      toast.error('Failed to run AI Buck on uncategorized', { id: toastId });
    } finally {
      setBulkAiBuckRunning(false);
    }
  };

  // Bulk Post Categorized - Preview
  const handleBulkPostCategorizedPreview = async () => {
    if (selectedIds.size === 0) {
      toast.error('No transactions selected');
      return;
    }
    
    setBulkPostLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/bulk-post-preview?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            transaction_ids: Array.from(selectedIds)
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setBulkPostPreview(data);
        setBulkActionDialog('post-categorized');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to preview bulk post');
      }
    } catch (error) {
      toast.error('Failed to preview bulk post');
    } finally {
      setBulkPostLoading(false);
    }
  };

  // Bulk Post Categorized - Execute
  const handleBulkPostCategorizedExecute = async () => {
    if (!bulkPostPreview || bulkPostPreview.eligible_count === 0) {
      toast.error('No eligible transactions to post');
      return;
    }
    
    setBulkProcessing(true);
    try {
      // Only post eligible transactions
      const eligibleIds = bulkPostPreview.items
        .filter(item => item.eligible)
        .map(item => item.transaction_id);
      
      const response = await fetch(
        `${API_URL}/api/banking/transactions/bulk-post-categorized?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            transaction_ids: eligibleIds
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.posted_count > 0) {
          toast.success(`${data.posted_count} journal entries created and posted`);
        }
        if (data.skipped_count > 0) {
          toast.info(`${data.skipped_count} transactions skipped`);
        }
        const justPosted = data.posted || [];
        setBulkActionDialog(null);
        setBulkPostPreview(null);
        setSelectedIds(new Set());
        setSelectAll(false);
        fetchTransactions();
        fetchAccounts();
        // After posting, offer to categorize+post similar uncategorized
        // transactions from the same merchant(s).
        try {
          // We need an account_id to apply. Pull it from the preview items
          // we just posted (they have category_name on them but not the id).
          // Easiest: look up the txn we just posted from the cached preview
          // and reuse its suggested_account_id via a fresh fetch.
          await maybeOfferSimilarFromPosted(justPosted);
        } catch { /* non-fatal */ }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Bulk post failed');
      }
    } catch (error) {
      toast.error('Bulk post failed');
    } finally {
      setBulkProcessing(false);
    }
  };

  // Quick Categorize - Open dialog with preview
  const handleQuickCategorizeOpen = () => {
    if (selectedIds.size === 0) {
      toast.error('No transactions selected');
      return;
    }
    // Reset form state
    setQuickCategorizeAccountId('');
    setQuickCategorizeContactId('');
    setQuickCategorizeMemo('');
    setQuickCategorizePreview(null);
    setBulkActionDialog('quick-categorize');
  };

  // Quick Categorize - Get preview when account is selected
  const handleQuickCategorizePreview = async (accountId) => {
    setQuickCategorizeAccountId(accountId);
    
    if (!accountId) {
      setQuickCategorizePreview(null);
      return;
    }
    
    setQuickCategorizeLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/transactions/bulk-categorize-preview?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            transaction_ids: Array.from(selectedIds),
            account_id: accountId
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setQuickCategorizePreview(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to preview');
      }
    } catch (error) {
      toast.error('Failed to preview categorization');
    } finally {
      setQuickCategorizeLoading(false);
    }
  };

  // ───────────────────── Apply Similar follow-up helpers ─────────────────────
  // After a user categorizes or posts one (or a few) transactions, we look
  // for OTHER uncategorized transactions from the same merchant and offer
  // them as a one-click batch follow-up. Saves dozens of repeated clicks
  // for recurring vendors like Starbucks, AT&T, Office Depot, etc.

  const _merchantOf = (txn) => {
    return (
      txn?.merchant_name ||
      txn?.description ||
      txn?.name ||
      ''
    ).trim();
  };

  // Probe the backend for similar uncategorized txns per merchant.
  const maybeOfferSimilar = async (categorizedItems, accountId, contactId, memo, mode) => {
    if (!categorizedItems || categorizedItems.length === 0 || !accountId) return;
    if (!currentWorkspace?.workspace_id) return;

    const merchants = Array.from(
      new Set(
        categorizedItems.map((c) => _merchantOf(c)).filter(Boolean),
      ),
    );
    const excludeIds = categorizedItems.map((c) => c.transaction_id).filter(Boolean);

    const allItems = [];
    let primaryMerchant = '';
    for (const m of merchants.slice(0, 5)) {
      try {
        const r = await fetch(
          `${API_URL}/api/banking/transactions/find-similar-uncategorized?workspace_id=${currentWorkspace.workspace_id}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              merchant_or_name: m,
              exclude_transaction_ids: excludeIds,
              limit: 50,
            }),
          },
        );
        if (!r.ok) continue;
        const data = await r.json();
        if (data && data.count > 0) {
          if (!primaryMerchant) primaryMerchant = data.merchant || m;
          for (const it of data.items) {
            allItems.push({ ...it, _merchant: data.merchant || m });
          }
        }
      } catch { /* ignore per-merchant errors */ }
    }
    if (allItems.length === 0) return;

    const matched = (accounts || []).find((a) => a.account_id === accountId);
    const accountName = matched ? `${matched.code} · ${matched.name}` : 'selected category';

    setSimilarPrompt({
      merchant: primaryMerchant || merchants[0] || '',
      items: allItems,
      accountId,
      accountName,
      contactId: contactId || null,
      memo: memo || null,
      mode: mode || 'categorize',
    });
  };

  // Variant used after Bulk Post Categorized.
  const maybeOfferSimilarFromPosted = async (postedItems) => {
    if (!postedItems || postedItems.length === 0) return;
    if (!currentWorkspace?.workspace_id) return;

    const groups = new Map();
    for (const p of postedItems) {
      try {
        const txnRes = await fetch(
          `${API_URL}/api/banking/transactions/${p.transaction_id}?workspace_id=${currentWorkspace.workspace_id}`,
          { credentials: 'include' },
        );
        if (!txnRes.ok) continue;
        const txn = await txnRes.json();
        const entryId = txn.journal_entry_id;
        if (!entryId) continue;
        const jeRes = await fetch(
          `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/journal-entries/${entryId}`,
          { credentials: 'include' },
        );
        if (!jeRes.ok) continue;
        const je = await jeRes.json();
        const categoryLine = (je.lines || []).find((l) => l.account_code !== '1000');
        if (!categoryLine) continue;
        const accountId = categoryLine.account_id;
        const merchant = _merchantOf({
          merchant_name: txn.merchant_name,
          name: txn.name,
        });
        if (!groups.has(accountId)) groups.set(accountId, { items: [] });
        groups.get(accountId).items.push({
          transaction_id: p.transaction_id,
          merchant_name: merchant,
        });
      } catch { /* keep going */ }
    }
    if (groups.size === 0) return;
    let bestAccountId = null;
    let bestItems = [];
    for (const [aid, g] of groups.entries()) {
      if (g.items.length > bestItems.length) {
        bestAccountId = aid;
        bestItems = g.items;
      }
    }
    if (bestAccountId) {
      await maybeOfferSimilar(bestItems, bestAccountId, null, null, 'post');
    }
  };

  // Apply the chosen action to the similar transactions surfaced by the prompt.
  const handleApplySimilar = async () => {
    if (!similarPrompt || !similarPrompt.items?.length) return;
    setSimilarProcessing(true);
    try {
      const txnIds = similarPrompt.items.map((i) => i.transaction_id);
      const catRes = await fetch(
        `${API_URL}/api/banking/transactions/bulk-categorize?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            transaction_ids: txnIds,
            account_id: similarPrompt.accountId,
            contact_id: similarPrompt.contactId,
            memo: similarPrompt.memo,
          }),
        },
      );
      if (!catRes.ok) {
        const err = await catRes.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to apply category to similar');
        return;
      }
      const catData = await catRes.json();

      if (similarPrompt.mode === 'post' && catData.categorized_count > 0) {
        const postRes = await fetch(
          `${API_URL}/api/banking/transactions/bulk-post-categorized?workspace_id=${currentWorkspace.workspace_id}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ transaction_ids: txnIds }),
          },
        );
        if (postRes.ok) {
          const postData = await postRes.json();
          toast.success(
            `Applied to ${catData.categorized_count} similar · ${postData.posted_count} posted`,
          );
        } else {
          toast.success(`Categorized ${catData.categorized_count} similar transactions`);
        }
        fetchAccounts();
      } else {
        toast.success(`Categorized ${catData.categorized_count} similar transactions`);
      }
      setSimilarPrompt(null);
      fetchTransactions();
    } catch (e) {
      toast.error('Failed to apply to similar');
    } finally {
      setSimilarProcessing(false);
    }
  };

  // Quick Categorize - Execute
  const handleQuickCategorizeExecute = async () => {
    if (!quickCategorizeAccountId) {
      toast.error('Please select a category');
      return;
    }
    
    if (!quickCategorizePreview || quickCategorizePreview.eligible_count === 0) {
      toast.error('No eligible transactions to categorize');
      return;
    }
    
    setBulkProcessing(true);
    try {
      // Only categorize eligible transactions
      const eligibleIds = quickCategorizePreview.items
        .filter(item => item.eligible)
        .map(item => item.transaction_id);
      
      const response = await fetch(
        `${API_URL}/api/banking/transactions/bulk-categorize?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            transaction_ids: eligibleIds,
            account_id: quickCategorizeAccountId,
            contact_id: quickCategorizeContactId && quickCategorizeContactId !== 'none' ? quickCategorizeContactId : null,
            memo: quickCategorizeMemo || null
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.categorized_count > 0) {
          toast.success(`${data.categorized_count} transactions categorized`);
        }
        if (data.skipped_count > 0) {
          toast.info(`${data.skipped_count} transactions skipped`);
        }
        // Capture context before clearing state so the "Apply Similar"
        // follow-up can use the same category + memo.
        const justCategorized = data.categorized || [];
        const accountIdUsed = quickCategorizeAccountId;
        const contactIdUsed = quickCategorizeContactId && quickCategorizeContactId !== 'none' ? quickCategorizeContactId : null;
        const memoUsed = quickCategorizeMemo || null;
        setBulkActionDialog(null);
        setQuickCategorizePreview(null);
        setQuickCategorizeAccountId('');
        setQuickCategorizeContactId('');
        setQuickCategorizeMemo('');
        setSelectedIds(new Set());
        setSelectAll(false);
        fetchTransactions();
        // Offer to apply the same category to other uncategorized txns from
        // the same merchant. We probe per-merchant (de-duped) so the prompt
        // doesn't fire for one-off transactions.
        try { await maybeOfferSimilar(justCategorized, accountIdUsed, contactIdUsed, memoUsed, 'categorize'); } catch { /* non-fatal */ }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Categorization failed');
      }
    } catch (error) {
      toast.error('Categorization failed');
    } finally {
      setBulkProcessing(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearch('');
    setDateFrom(null);
    setDateTo(null);
    setTxnType('all');
    setAmountMin('');
    setAmountMax('');
  };

  const hasFilters = search || dateFrom || dateTo || txnType !== 'all' || amountMin || amountMax;

  // Calculate totals for account carousel
  const totalBalance = useMemo(() => 
    bankAccounts.reduce((sum, b) => sum + b.current_balance, 0), 
    [bankAccounts]
  );
  const totalPending = useMemo(() => 
    bankAccounts.reduce((sum, b) => sum + b.pending_count, 0), 
    [bankAccounts]
  );

  return (
    <div className="space-y-4" data-testid="banking-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Banking</h1>
          <p className="text-sm text-muted-foreground">
            Review and categorize bank transactions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchTransactions(); fetchAccounts(); }}>
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Refresh
          </Button>
          {selectedAccountIsManual && activeTab === 'pending' && (
            <Button variant="outline" size="sm" onClick={openManualTxnDrawer} data-testid="create-manual-txn-btn">
              <PlusCircle className="w-3.5 h-3.5 mr-1.5" />
              Create Transaction
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={openAddAccountDrawer} data-testid="add-account-btn">
            <Wallet className="w-3.5 h-3.5 mr-1.5" />
            Add Account
          </Button>
          <Button variant="outline" size="sm" asChild data-testid="upload-statement-btn">
            <Link to="/banking/statement-upload">
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              Upload Statement
            </Link>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setScannerDialogOpen(true)} data-testid="scan-statement-btn"
            className="border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20">
            <ScanLine className="w-3.5 h-3.5 mr-1.5" />
            Scan Statement (AI)
          </Button>
          <Button variant="outline" size="sm" asChild data-testid="reconcile-btn">
            <Link to="/banking/reconcile">
              <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
              Reconcile
            </Link>
          </Button>
          <Button size="sm" asChild>
            <Link to="/ai-buck">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              AI Buck
            </Link>
          </Button>
          <HelpPanel {...bankingHelp} />
        </div>
      </div>

      {/* AI Buck Banner */}
      {aiBuckCount > 0 && activeTab === 'pending' && (
        <div 
          className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20"
          data-testid="ai-buck-banner"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-primary/10">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">
                AI Buck found {aiBuckCount} transaction{aiBuckCount !== 1 ? 's' : ''} to review
              </p>
              <p className="text-xs text-muted-foreground">
                AI Buck suggests categories—review and approve when ready
              </p>
            </div>
          </div>
          <Button size="sm" asChild>
            <Link to={`/ai-buck${selectedBankId !== 'all' ? `?bank=${selectedBankId}` : ''}`}>
              Review
              <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Link>
          </Button>
        </div>
      )}

      {/* Bank Rules status bar — shows active auto-categorization rules
          so the user always knows which transactions will be silently
          categorized on the next bank sync. Click "Manage rules" to
          view/edit them on the AI Buck Rules tab. */}
      <BankRulesStatusBar workspaceId={currentWorkspace?.workspace_id} />

      {/* Agent OS Smart Suggestion — deep-links into the CFO Buck Agent
          Tab and auto-launches the Categorize skill so the user lands in
          the "Action Receipt" flow (auto-apply / review / ask buckets).
          Distinct from the inline AI Buck banner above which uses the
          per-row pending suggestions surface. */}
      {activeTab === 'pending' && uncategorizedTxnIds.length >= 3 && (
        <div
          className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-violet-50 to-fuchsia-50 dark:from-violet-950/30 dark:to-fuchsia-950/20 border border-violet-200 dark:border-violet-900/50"
          data-testid="agent-smart-suggest-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-full bg-violet-100 dark:bg-violet-900/40">
              <Wand2 className="w-4 h-4 text-violet-600 dark:text-violet-300" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">
                Let AI Buck handle this batch end-to-end
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {uncategorizedTxnIds.length} uncategorized — AI Buck will plan, auto-apply the safe ones, and ask about the rest.
              </p>
            </div>
          </div>
          <Button size="sm" asChild className="bg-violet-600 hover:bg-violet-700 text-white shrink-0">
            <Link to="/cfo-buck?tab=agent&launch=categorize" data-testid="agent-smart-suggest-launch">
              Launch agent
              <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Link>
          </Button>
        </div>
      )}

      {/* Auto-suggest All Uncategorized Banner — appears when there are
          uncategorized txns in view that don't yet have an AI Buck
          suggestion. One click runs the categorizer over every one. */}
      {activeTab === 'pending' && uncategorizedTxnIds.length > 0 && (
        <div
          className="flex items-center justify-between p-3 rounded-lg bg-amber-50 dark:bg-amber-900/15 border border-amber-200/70 dark:border-amber-800/40"
          data-testid="auto-suggest-uncategorized-banner"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/30">
              <Sparkles className="w-4 h-4 text-amber-700 dark:text-amber-300" />
            </div>
            <div>
              <p className="text-sm font-medium">
                {uncategorizedTxnIds.length} uncategorized transaction{uncategorizedTxnIds.length === 1 ? '' : 's'} without a suggestion
              </p>
              <p className="text-xs text-muted-foreground">
                Let AI Buck propose categories for the whole batch — saves 20+ minutes of click-by-click work.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 text-white"
            onClick={() => handleBulkRunAiBuck(uncategorizedTxnIds)}
            disabled={bulkAiBuckRunning}
            data-testid="bulk-run-ai-buck-btn"
          >
            {bulkAiBuckRunning ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Running…</>
            ) : (
              <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Auto-suggest all</>
            )}
          </Button>
        </div>
      )}

      {/* Account Carousel */}
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Bank Accounts</span>
            {/* Show-archived toggle — surfaces whenever any account is
                archived (even when the default list hides them) so the
                user can always find their way back. */}
            {(showArchivedAccounts || archivedAccountCount > 0 || bankAccounts.some(a => a.is_archived)) && (
              <button
                type="button"
                onClick={() => setShowArchivedAccounts(v => !v)}
                className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline inline-flex items-center gap-1"
                data-testid="bank-show-archived-toggle"
              >
                <Archive className="w-3 h-3" />
                {showArchivedAccounts
                  ? 'Hide archived'
                  : `Show archived${archivedAccountCount > 0 ? ` (${archivedAccountCount})` : ''}`}
              </button>
            )}
          </div>
          {/* Master Sync-All — fires every Plaid item in parallel via
              /banking/accounts/sync-all (FREE endpoint). One click instead
              of clicking Sync on every bank card. */}
          {bankAccounts.some(a => a.source !== 'manual' && !a.is_archived) && (
            <Button
              size="sm" variant="outline"
              disabled={syncingAll}
              onClick={handleSyncAllBanks}
              data-testid="banking-sync-all-btn"
            >
              <RefreshCw className={`w-3 h-3 mr-1.5 ${syncingAll ? 'animate-spin' : ''}`} />
              {syncingAll ? 'Syncing all…' : 'Sync all'}
            </Button>
          )}
          {isAdmin && bankAccounts.some(a => a.source === 'plaid' && (
            !a.institution_logo_url ||
            (a.institution_name || '').toLowerCase() === 'unknown bank'
          )) && (
            <button
              type="button"
              onClick={handleRefreshLogos}
              disabled={refreshingLogos}
              className="text-[10px] uppercase tracking-wide px-2 py-1 rounded border border-border bg-muted/40 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              data-testid="refresh-bank-logos-btn"
            >
              {refreshingLogos ? 'Refreshing…' : 'Refresh logos'}
            </button>
          )}
        </div>

        <RelinkBankBanner
          bankAccounts={bankAccounts}
          onReconnected={fetchAccounts}
        />

        {accountsLoading ? (
          <div className="flex gap-3 overflow-hidden">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="w-56 h-24 flex-shrink-0" />
            ))}
          </div>
        ) : bankAccounts.length > 0 ? (
          <ScrollArea className="w-full pb-2">
            <div className="flex gap-3" data-testid="account-carousel">
              {/* All Accounts Card */}
              <button
                onClick={() => setSelectedBankId('all')}
                className={cn(
                  "flex-shrink-0 w-44 p-3 rounded-lg border transition-all text-left",
                  selectedBankId === 'all'
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border bg-card hover:border-primary/50"
                )}
                data-testid="bank-card-all"
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <Building2 className="w-4 h-4 text-primary" />
                  <span className="font-medium text-xs">All Accounts</span>
                </div>
                <p className="text-base font-mono font-bold">
                  {formatCurrency(totalBalance)}
                </p>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {totalPending} pending
                </p>
              </button>
              
              {bankAccounts.map((account) => (
                <AccountCard
                  key={account.bank_id}
                  account={account}
                  isSelected={selectedBankId === account.bank_id}
                  onSelect={setSelectedBankId}
                  onArchive={handleArchiveBankAccount}
                  onSyncNow={handleSyncBankNow}
                />
              ))}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        ) : (
          <BrandEmptyState
            overline="Banking · Get Started"
            title="Connect your first bank."
            accent="bank."
            description="Auto-sync transactions from 11,000+ banks via Plaid. Reconcile in seconds, categorize with AI, never touch CSV exports again."
            icon={Building2}
            tone="emerald"
            primaryCta={{
              label: 'Connect a bank',
              onClick: () => window.dispatchEvent(new CustomEvent('open-setup-wizard', { detail: { startStep: 0 } })),
              testId: 'empty-connect-bank',
            }}
            secondaryCta={{ label: 'Add manually', onClick: () => setAddAccountDrawerOpen(true), testId: 'empty-manual-bank' }}
            testId="banking-empty-state"
          />
        )}
      </div>

      {/* Tabs and Filters */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex items-center gap-2">
            <TabsList className="h-9" data-testid="transaction-tabs">
              <TabsTrigger value="pending" className="text-xs px-3" data-testid="tab-pending">
                Pending
                <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0 h-4">
                  {tabCounts.pending}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="posted" className="text-xs px-3" data-testid="tab-posted">
                Posted
                <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0 h-4">
                  {tabCounts.posted}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="excluded" className="text-xs px-3" data-testid="tab-excluded">
                Excluded
                <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0 h-4">
                  {tabCounts.excluded}
                </Badge>
              </TabsTrigger>
            </TabsList>
            
            {/* Status Guide Popover */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 px-2 text-muted-foreground hover:text-foreground" data-testid="status-guide-btn">
                  <HelpCircle className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80" align="start">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-primary" />
                    <h4 className="font-semibold text-sm">Transaction Status Guide</h4>
                  </div>
                  
                  <div className="space-y-3">
                    {/* For Review */}
                    <div className="p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-xs flex items-center gap-1.5">
                          <Eye className="w-3.5 h-3.5 text-amber-600" />
                          For Review
                        </span>
                        <Badge variant="outline" className="text-[10px] h-4">{tabCounts.forReview || 0}</Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground mb-1">
                        Transaction imported or entered but not yet categorized.
                      </p>
                      <p className="text-[10px] text-amber-700 dark:text-amber-400">
                        <strong>Next:</strong> Categorize via Quick Categorize or AI Buck, then post.
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        <strong>Dashboard:</strong> Not included in KPIs until posted.
                      </p>
                    </div>
                    
                    {/* Categorized */}
                    <div className="p-2.5 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-xs flex items-center gap-1.5">
                          <Tags className="w-3.5 h-3.5 text-blue-600" />
                          Categorized
                        </span>
                        <Badge variant="outline" className="text-[10px] h-4">{tabCounts.categorized || 0}</Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground mb-1">
                        Category selected, ready to post to the general ledger.
                      </p>
                      <p className="text-[10px] text-blue-700 dark:text-blue-400">
                        <strong>Next:</strong> Click Post Entry or use Bulk Post.
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        <strong>Dashboard:</strong> Not included until posted.
                      </p>
                    </div>
                    
                    {/* Posted */}
                    <div className="p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-xs flex items-center gap-1.5">
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                          Posted
                        </span>
                        <Badge variant="outline" className="text-[10px] h-4">{tabCounts.posted}</Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground mb-1">
                        Journal entry created and posted. Impacts financial statements.
                      </p>
                      <p className="text-[10px] text-emerald-700 dark:text-emerald-400">
                        <strong>Next:</strong> None required. View linked journal entry if needed.
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        <strong>Dashboard:</strong> ✓ Included in all KPIs.
                      </p>
                    </div>
                    
                    {/* Excluded */}
                    <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-950/30 border border-gray-200 dark:border-gray-800">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-xs flex items-center gap-1.5">
                          <EyeOff className="w-3.5 h-3.5 text-gray-500" />
                          Excluded
                        </span>
                        <Badge variant="outline" className="text-[10px] h-4">{tabCounts.excluded}</Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground mb-1">
                        Intentionally ignored. Will not be posted to books.
                      </p>
                      <p className="text-[10px] text-gray-600 dark:text-gray-400">
                        <strong>Next:</strong> None. Restore if excluded by mistake.
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        <strong>Dashboard:</strong> Not included.
                      </p>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 w-48 text-sm"
                data-testid="txn-search"
              />
            </div>
            
            {/* Filter Toggle */}
            <Button 
              variant={showFilters ? 'secondary' : 'outline'} 
              size="sm"
              className="h-8"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-3.5 h-3.5 mr-1.5" />
              Filters
              {hasFilters && (
                <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-primary" />
              )}
            </Button>
            
            {hasFilters && (
              <Button variant="ghost" size="sm" className="h-8" onClick={clearFilters}>
                <X className="w-3.5 h-3.5 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Expanded Filters Panel - Resizable */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <Card 
                ref={filterPanelRef}
                className="p-3 relative"
                style={{ minWidth: filterPanelWidth }}
              >
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Date From</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full justify-start text-left font-normal h-8 text-xs">
                          <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
                          {dateFrom ? format(dateFrom, 'MM/dd/yyyy') : 'Select'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Date To</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full justify-start text-left font-normal h-8 text-xs">
                          <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
                          {dateTo ? format(dateTo, 'MM/dd/yyyy') : 'Select'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={dateTo} onSelect={setDateTo} />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Type</Label>
                    <Select value={txnType} onValueChange={setTxnType}>
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="spend">Spent</SelectItem>
                        <SelectItem value="receive">Received</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Min Amount</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={amountMin}
                      onChange={(e) => setAmountMin(e.target.value)}
                      className="h-8 text-xs"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Max Amount</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={amountMax}
                      onChange={(e) => setAmountMax(e.target.value)}
                      className="h-8 text-xs"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Sort By</Label>
                    <Select value={sortBy} onValueChange={(value) => setSortBy(value)} data-testid="sort-by-select">
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="date">Date</SelectItem>
                        <SelectItem value="name">Description</SelectItem>
                        <SelectItem value="account">Account</SelectItem>
                        <SelectItem value="spent">Spent</SelectItem>
                        <SelectItem value="received">Received</SelectItem>
                        <SelectItem value="status">Status</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Sort Direction</Label>
                    <Select value={sortDir} onValueChange={(value) => setSortDir(value)} data-testid="sort-dir-select">
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="desc">
                          <span className="flex items-center gap-1.5">
                            <ChevronDown className="w-3 h-3" /> Descending
                          </span>
                        </SelectItem>
                        <SelectItem value="asc">
                          <span className="flex items-center gap-1.5">
                            <ChevronUp className="w-3 h-3" /> Ascending
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Drag Handle for Resize */}
                <div 
                  className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize flex items-center justify-center hover:bg-muted/50 transition-colors group"
                  onMouseDown={handleMouseDown}
                  data-testid="filter-panel-resize-handle"
                >
                  <GripVertical className="w-3 h-3 text-muted-foreground/50 group-hover:text-muted-foreground" />
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && activeTab !== 'posted' && (
          <div className="flex items-center justify-between p-2.5 rounded-lg bg-primary/5 border border-primary/20">
            <div className="flex items-center gap-2">
              <CheckSquare className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">{selectedIds.size} selected</span>
            </div>
            <div className="flex items-center gap-2">
              {activeTab === 'pending' && (
                <>
                  <Button 
                    size="sm" 
                    className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleBulkPostCategorizedPreview}
                    disabled={bulkProcessing || bulkPostLoading}
                    data-testid="bulk-post-categorized-btn"
                  >
                    {bulkPostLoading ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Checking...</>
                    ) : (
                      <><CheckCircle className="w-3.5 h-3.5 mr-1" /> Bulk Post</>
                    )}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="secondary"
                    className="h-7 text-xs"
                    onClick={handleQuickCategorizeOpen}
                    disabled={bulkProcessing || quickCategorizeLoading}
                    data-testid="quick-categorize-btn"
                  >
                    <Tags className="w-3.5 h-3.5 mr-1" />
                    Quick Categorize
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => handleBulkAction('post')}
                    disabled={bulkProcessing}
                    data-testid="bulk-post-btn"
                  >
                    <PlusCircle className="w-3.5 h-3.5 mr-1" />
                    Post All (Select Account)
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => handleBulkAction('exclude')}
                    disabled={bulkProcessing}
                    data-testid="bulk-exclude-btn"
                  >
                    <Ban className="w-3.5 h-3.5 mr-1" />
                    Exclude All
                  </Button>
                </>
              )}
              {activeTab === 'excluded' && (
                <Button 
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => handleBulkAction('restore')}
                  disabled={bulkProcessing}
                  data-testid="bulk-restore-btn"
                >
                  <Undo2 className="w-3.5 h-3.5 mr-1" />
                  Restore All
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="sm"
                className="h-7 text-xs"
                onClick={() => { setSelectedIds(new Set()); setSelectAll(false); }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Transactions Table */}
        <TabsContent value={activeTab} className="mt-0">
          <Card className="overflow-hidden">
            {/* Header Filters Indicator */}
            {hasHeaderFilters && (
              <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Filter className="w-3.5 h-3.5" />
                  <span>Header filters active:</span>
                  {statusFilter.length > 0 && (
                    <Badge variant="secondary" className="text-[10px]">
                      Status: {statusFilter.length} selected
                    </Badge>
                  )}
                  {descriptionFilter && (
                    <Badge variant="secondary" className="text-[10px]">
                      Description: &ldquo;{descriptionFilter}&rdquo;
                    </Badge>
                  )}
                  {selectedVendors.length > 0 && (
                    <Badge variant="secondary" className="text-[10px]">
                      Vendors: {selectedVendors.length} selected
                    </Badge>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs text-primary hover:text-primary/80"
                  onClick={clearHeaderFilters}
                  data-testid="clear-header-filters"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear
                </Button>
              </div>
            )}
            
            {loading ? (
              <div className="p-4 space-y-3">
                {[...Array(8)].map((_, i) => (
                  <Skeleton key={i} className="h-10" />
                ))}
              </div>
            ) : filteredByCategory.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow className="border-b">
                      {activeTab !== 'posted' && (
                        <TableHead className="w-10 px-3">
                          <Checkbox
                            checked={selectAll}
                            onCheckedChange={handleSelectAll}
                            data-testid="select-all"
                          />
                        </TableHead>
                      )}
                      <SortableHeader
                        label="Date"
                        field="date"
                        currentSort={sortBy}
                        currentDir={sortDir}
                        onSort={handleSort}
                        className="w-24"
                      />
                      <DescriptionFilterHeader
                        searchValue={descriptionFilter}
                        onSearchChange={setDescriptionFilter}
                        selectedVendors={selectedVendors}
                        onVendorsChange={setSelectedVendors}
                        topVendors={topVendors}
                        currentSort={sortBy}
                        currentDir={sortDir}
                        onSort={handleSort}
                        open={descriptionPopoverOpen}
                        onOpenChange={setDescriptionPopoverOpen}
                        onApply={() => {}}
                      />
                      <CategoryFilterHeader
                        accounts={accounts}
                        selected={categoryFilter}
                        onApply={setCategoryFilter}
                        currentSort={sortBy}
                        currentDir={sortDir}
                        onSort={handleSort}
                        open={categoryPopoverOpen}
                        onOpenChange={setCategoryPopoverOpen}
                      />
                      <SortableHeader
                        label="Spent"
                        field="spent"
                        currentSort={sortBy}
                        currentDir={sortDir}
                        onSort={handleSort}
                        className="w-24 text-right"
                        align="right"
                      />
                      <SortableHeader
                        label="Received"
                        field="received"
                        currentSort={sortBy}
                        currentDir={sortDir}
                        onSort={handleSort}
                        className="w-24 text-right"
                        align="right"
                      />
                      <StatusFilterHeader
                        selected={statusFilter}
                        onApply={setStatusFilter}
                        currentSort={sortBy}
                        currentDir={sortDir}
                        onSort={handleSort}
                        open={statusPopoverOpen}
                        onOpenChange={setStatusPopoverOpen}
                      />
                      <TableHead className="w-28 text-xs font-medium text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredByCategory.map((txn) => (
                      <React.Fragment key={txn.transaction_id}>
                        <TableRow 
                          className={cn(
                            "cursor-pointer hover:bg-muted/50 h-11",
                            expandedRow === txn.transaction_id && "bg-muted/30",
                            selectedIds.has(txn.transaction_id) && "bg-primary/5"
                          )}
                          data-testid={`txn-row-${txn.transaction_id}`}
                        >
                          {activeTab !== 'posted' && (
                            <TableCell className="px-3" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center gap-0.5">
                                <Checkbox
                                  checked={selectedIds.has(txn.transaction_id)}
                                  onCheckedChange={() => handleSelectRow(txn.transaction_id)}
                                />
                                {/* Leading chevron — mirrors AI Buck UX. Lets users
                                    expand without hunting for the chevron at the
                                    right edge of the row. */}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                                  onClick={() => toggleRow(txn.transaction_id)}
                                  aria-label={expandedRow === txn.transaction_id ? 'Collapse' : 'Expand'}
                                  data-testid={`expand-leading-${txn.transaction_id}`}
                                  title="Expand transaction details"
                                >
                                  {expandedRow === txn.transaction_id ? (
                                    <ChevronDown className="w-3.5 h-3.5" />
                                  ) : (
                                    <ChevronRight className="w-3.5 h-3.5" />
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          )}
                          <TableCell 
                            className="font-mono text-xs"
                            onClick={() => toggleRow(txn.transaction_id)}
                          >
                            {formatDate(txn.date)}
                          </TableCell>
                          <TableCell onClick={() => toggleRow(txn.transaction_id)}>
                            <div className="flex items-center gap-2">
                              <div className={cn(
                                "w-6 h-6 rounded flex items-center justify-center flex-shrink-0",
                                txn.amount > 0 ? "bg-red-100 dark:bg-red-900/30" : "bg-emerald-100 dark:bg-emerald-900/30"
                              )}>
                                {txn.amount > 0 ? (
                                  <ArrowUpRight className="w-3 h-3 text-red-600 dark:text-red-400" />
                                ) : (
                                  <ArrowDownLeft className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs font-medium truncate max-w-[180px]">
                                  {txn.merchant_name || txn.name}
                                </p>
                              </div>
                              {txn.suggested_account_name && activeTab === 'pending' && (
                                <div className="hidden lg:flex items-center gap-1 ml-1">
                                  <Sparkles className="w-3 h-3 text-primary" />
                                  <ConfidencePill confidence={txn.confidence} />
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            {/* Inline category dropdown. Pending → updates the
                                suggested category. Posted → triggers the
                                user-friendly recategorize flow (silently
                                reverses the old JE + creates a new one — no
                                JE jargon surfaces). */}
                            <InlineCategorySelect
                              value={txn.suggested_account_id || ''}
                              accounts={accounts}
                              ghost
                              testId={`category-select-${txn.transaction_id}`}
                              onChange={(newId) => {
                                if (activeTab === 'posted') {
                                  handleRecategorizePosted(txn, newId);
                                } else {
                                  handleRowCategoryChange(txn, newId);
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell 
                            className="text-right font-mono text-xs"
                            onClick={() => toggleRow(txn.transaction_id)}
                          >
                            {txn.amount > 0 && (
                              <span className="text-red-600 dark:text-red-400">
                                {formatCurrency(txn.amount)}
                              </span>
                            )}
                          </TableCell>
                          <TableCell 
                            className="text-right font-mono text-xs"
                            onClick={() => toggleRow(txn.transaction_id)}
                          >
                            {txn.amount < 0 && (
                              <span className="text-emerald-600 dark:text-emerald-400">
                                {formatCurrency(txn.amount)}
                              </span>
                            )}
                          </TableCell>
                          <TableCell onClick={() => toggleRow(txn.transaction_id)}>
                            <div className="flex items-center gap-1">
                              <TransactionStatusBadge 
                                status={txn.status} 
                                hasCategoryAssigned={!!(txn.suggested_account_id || txn.category_account_id || txn.journal_entry_id)}
                              />
                              {txn.has_split_draft && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800">
                                  Split Draft
                                </Badge>
                              )}
                              {txn.has_split_posted && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800">
                                  Split
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-0.5">
                              {activeTab === 'pending' && (
                                <>
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={(e) => { e.stopPropagation(); handleFindMatch(txn); }}
                                    title="Match"
                                    data-testid={`match-${txn.transaction_id}`}
                                  >
                                    <Link2 className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={(e) => { e.stopPropagation(); handleOpenCreateEntry(txn); }}
                                    title="Categorize"
                                    data-testid={`categorize-${txn.transaction_id}`}
                                  >
                                    <Tags className="w-3.5 h-3.5" />
                                  </Button>
                                  {txn.has_split_draft ? (
                                    <Button 
                                      size="sm"
                                      className="h-7 text-xs"
                                      onClick={(e) => { e.stopPropagation(); postExistingSplit(txn); }}
                                      title="Post Split"
                                      data-testid={`post-split-${txn.transaction_id}`}
                                    >
                                      <SplitSquareVertical className="w-3.5 h-3.5 mr-1" />
                                      Post Split
                                    </Button>
                                  ) : (
                                    <Button 
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={(e) => { e.stopPropagation(); handleQuickPost(txn); }}
                                      disabled={quickPostingId === txn.transaction_id}
                                      title={txn.suggested_account_id
                                        ? `Post to "${txn.suggested_account_name || 'category'}" (one click)`
                                        : 'Pick a category first, then click to post'}
                                      data-testid={`post-${txn.transaction_id}`}
                                    >
                                      {quickPostingId === txn.transaction_id
                                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        : <PlusCircle className="w-3.5 h-3.5" />
                                      }
                                    </Button>
                                  )}
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-7 w-7">
                                        <MoreHorizontal className="w-3.5 h-3.5" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => openSplitDrawer(txn)}>
                                        <SplitSquareVertical className="w-3.5 h-3.5 mr-2" />
                                        {txn.has_split_draft ? 'Edit Split' : 'Split Transaction'}
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onClick={() => handleCreateRuleFromTxn(txn, 'merchant')}
                                        disabled={!txn.suggested_account_id}
                                        data-testid={`rule-from-merchant-${txn.transaction_id}`}
                                      >
                                        <Zap className="w-3.5 h-3.5 mr-2" />
                                        Create rule from merchant
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={() => handleCreateRuleFromTxn(txn, 'description')}
                                        disabled={!txn.suggested_account_id}
                                        data-testid={`rule-from-description-${txn.transaction_id}`}
                                      >
                                        <Zap className="w-3.5 h-3.5 mr-2" />
                                        Create rule from description
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem onClick={() => handleExclude(txn)}>
                                        <Ban className="w-3.5 h-3.5 mr-2" />
                                        Exclude
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </>
                              )}
                              {activeTab === 'posted' && txn.journal_entry_id && (
                                <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                                  <Link to={`/journal-entries/${txn.journal_entry_id}`}>
                                    <ExternalLink className="w-3.5 h-3.5" />
                                  </Link>
                                </Button>
                              )}
                              {activeTab === 'excluded' && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={(e) => { e.stopPropagation(); handleRestore(txn); }}
                                  data-testid={`restore-${txn.transaction_id}`}
                                >
                                  <Undo2 className="w-3 h-3 mr-1" />
                                  Restore
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        
                        {/* Expanded Row Detail - Enhanced with framer-motion */}
                        <AnimatePresence>
                          {expandedRow === txn.transaction_id && (
                            <TableRow className="bg-muted/10 hover:bg-muted/10">
                              <TableCell colSpan={activeTab !== 'posted' ? 8 : 7} className="p-0">
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.25, ease: 'easeInOut' }}
                                  className="overflow-hidden"
                                >
                                  {loadingDetail ? (
                                    <div className="p-4 space-y-3">
                                      <Skeleton className="h-24" />
                                      <Skeleton className="h-16" />
                                    </div>
                                  ) : expandedDetail ? (
                                    <div className="p-4 space-y-4">
                                      {/* Transaction Details Grid - Enhanced */}
                                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 p-3 rounded-lg border bg-card">
                                        <div>
                                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Full Description</p>
                                          <p className="text-xs font-medium mt-0.5">{expandedDetail.name}</p>
                                        </div>
                                        <div>
                                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Merchant</p>
                                          <p className="text-xs font-medium mt-0.5">{expandedDetail.merchant_name || 'N/A'}</p>
                                        </div>
                                        <div>
                                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Bank Category</p>
                                          <p className="text-xs font-medium mt-0.5 text-muted-foreground">{expandedDetail.category?.join(' > ') || '—'}</p>
                                        </div>
                                        <div>
                                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Bank Account</p>
                                          <p className="text-xs font-medium mt-0.5">{expandedDetail.bank_name} {expandedDetail.bank_mask && `••${expandedDetail.bank_mask}`}</p>
                                        </div>
                                        {/* NEW: Bank Reference */}
                                        <div>
                                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                                            <Hash className="w-3 h-3" />
                                            Bank Reference
                                          </p>
                                          <p className="text-xs font-mono mt-0.5">{expandedDetail.bank_reference || expandedDetail.transaction_id?.slice(-8) || 'N/A'}</p>
                                        </div>
                                        {/* NEW: Posted Date */}
                                        <div>
                                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                                            <CalendarDays className="w-3 h-3" />
                                            Posted Date
                                          </p>
                                          <p className="text-xs font-medium mt-0.5">{expandedDetail.posted_date ? formatDate(expandedDetail.posted_date) : formatDate(expandedDetail.date)}</p>
                                        </div>
                                      </div>
                                      
                                      {/* NEW: Additional Bank Details Row */}
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 rounded-lg border bg-muted/30">
                                        <div>
                                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                                            <CheckNumber className="w-3 h-3" />
                                            Check Number
                                          </p>
                                          <p className="text-xs font-mono mt-0.5">{expandedDetail.check_number || '—'}</p>
                                        </div>
                                        <div>
                                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Payment Channel</p>
                                          <p className="text-xs font-medium mt-0.5 capitalize">{expandedDetail.payment_channel || expandedDetail.payment_method || 'Electronic'}</p>
                                        </div>
                                        <div>
                                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Transaction Type</p>
                                          <Badge variant="outline" className="mt-0.5 text-[10px]">
                                            {txn.amount > 0 ? 'Outflow (Spend)' : 'Inflow (Receive)'}
                                          </Badge>
                                        </div>
                                        <div>
                                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Source</p>
                                          <p className="text-xs font-medium mt-0.5 capitalize">{expandedDetail.source || 'Plaid'}</p>
                                        </div>
                                      </div>
                                      
                                      {/* Inline Categorization & Post - Only for pending transactions */}
                                      {activeTab === 'pending' && txn.status !== 'posted' && !expandedDetail.journal_entry && (
                                        <motion.div 
                                          initial={{ opacity: 0, y: -10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          transition={{ delay: 0.1 }}
                                          className="p-3 rounded-lg border-2 border-primary/30 bg-primary/5"
                                        >
                                          <div className="flex items-center gap-2 mb-3">
                                            <Tags className="w-4 h-4 text-primary" />
                                            <span className="text-xs font-medium">Quick Categorize & Post</span>
                                          </div>
                                          
                                          {/* Bank Account Validation */}
                                          {!expandedDetail.bank_id ? (
                                            <div className="flex items-center gap-2 p-2 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
                                              <AlertCircle className="w-4 h-4" />
                                              <span className="text-xs">No bank account linked. Cannot post this transaction.</span>
                                            </div>
                                          ) : (
                                            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
                                              <div className="flex-1 space-y-1.5 w-full sm:w-auto">
                                                <Label className="text-[10px] text-muted-foreground">Category Account</Label>
                                                <SearchableAccountSelect
                                                  accounts={accounts}
                                                  value={inlineCategoryId}
                                                  onValueChange={setInlineCategoryId}
                                                  placeholder="Search accounts..."
                                                  className="h-8 text-xs"
                                                  testId="inline-category-select"
                                                  filterTypes={txn.amount > 0 ? ['expense', 'asset'] : ['income', 'liability']}
                                                />
                                              </div>
                                              
                                              <div className="flex items-center gap-2">
                                                <div className="text-[10px] text-muted-foreground hidden sm:block">
                                                  <span className="font-medium">Bank:</span> {expandedDetail.bank_name || 'Unknown'}
                                                </div>
                                                <Button
                                                  size="sm"
                                                  className="h-8 text-xs"
                                                  onClick={() => handleInlinePost(txn, inlineCategoryId)}
                                                  disabled={!inlineCategoryId || inlinePosting}
                                                  data-testid="inline-post-btn"
                                                >
                                                  {inlinePosting ? (
                                                    <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                                                  ) : (
                                                    <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                                  )}
                                                  Post
                                                </Button>
                                              </div>
                                            </div>
                                          )}
                                          
                                          {/* Helper text */}
                                          <p className="text-[10px] text-muted-foreground mt-2">
                                            Select a category and click Post to create a journal entry. For splits or advanced options, use the full Post Entry drawer.
                                          </p>
                                        </motion.div>
                                      )}
                                      
                                      {/* AI Buck Rationale */}
                                      {expandedDetail.ai_suggestion && (
                                        <motion.div 
                                          initial={{ opacity: 0, y: -10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          transition={{ delay: 0.15 }}
                                          className="p-3 rounded-lg border bg-primary/5 border-primary/20"
                                        >
                                          <div className="flex items-center gap-2 mb-2">
                                            <Sparkles className="w-4 h-4 text-primary" />
                                            <span className="text-xs font-medium">AI Suggestion</span>
                                            <ConfidencePill confidence={expandedDetail.ai_suggestion.confidence} />
                                          </div>
                                          <p className="text-xs">
                                            <span className="text-muted-foreground">Suggested: </span>
                                            <span className="font-mono">{expandedDetail.ai_suggestion.suggested_account_code}</span> - {expandedDetail.ai_suggestion.suggested_account_name}
                                          </p>
                                          {expandedDetail.ai_suggestion.rationale && (
                                            <p className="text-[10px] text-muted-foreground mt-1">
                                              {expandedDetail.ai_suggestion.rationale}
                                            </p>
                                          )}
                                        </motion.div>
                                      )}
                                  
                                  {/* Linked Journal Entry */}
                                  {expandedDetail.journal_entry && (
                                    <div className="p-3 rounded-lg border bg-card">
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                          <FileText className="w-4 h-4 text-muted-foreground" />
                                          <span className="text-xs font-medium">Journal Entry #{expandedDetail.journal_entry.entry_no}</span>
                                          <Badge variant="outline" className="text-[10px]">{expandedDetail.journal_entry.status}</Badge>
                                        </div>
                                        <Button variant="ghost" size="sm" className="h-6 text-xs" asChild>
                                          <Link to={`/journal-entries/${expandedDetail.journal_entry.entry_id}`}>
                                            View <ExternalLink className="w-3 h-3 ml-1" />
                                          </Link>
                                        </Button>
                                      </div>
                                      <Table>
                                        <TableHeader>
                                          <TableRow className="h-7">
                                            <TableHead className="text-[10px]">Account</TableHead>
                                            <TableHead className="text-[10px] text-right">Debit</TableHead>
                                            <TableHead className="text-[10px] text-right">Credit</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {expandedDetail.journal_entry.lines?.map((line, idx) => (
                                            <TableRow key={idx} className="h-7">
                                              <TableCell className="text-xs">
                                                <span className="font-mono text-[10px]">{line.account_code}</span> {line.account_name}
                                              </TableCell>
                                              <TableCell className="text-xs text-right font-mono">
                                                {line.debit > 0 ? formatCurrency(line.debit) : ''}
                                              </TableCell>
                                              <TableCell className="text-xs text-right font-mono">
                                                {line.credit > 0 ? formatCurrency(line.credit) : ''}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </div>
                                  )}
                                  
                                  {/* Attachments and Notes side by side */}
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Attachments */}
                                    <div className="p-3 rounded-lg border bg-card">
                                      <div className="flex items-center gap-2 mb-2">
                                        <Paperclip className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-xs font-medium">Attachments</span>
                                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                          {expandedDetail.attachments?.length || 0}
                                        </Badge>
                                      </div>
                                      <Attachments
                                        linkType="bank_transaction"
                                        linkId={expandedRow}
                                        title=""
                                        canUpload={activeTab !== 'posted'}
                                        canDelete={activeTab !== 'posted'}
                                        compact={true}
                                      />
                                    </div>
                                    
                                    {/* Notes Timeline */}
                                    <div className="p-3 rounded-lg border bg-card">
                                      <div className="flex items-center gap-2 mb-2">
                                        <MessageSquare className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-xs font-medium">Notes</span>
                                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                          {expandedDetail.notes?.length || 0}
                                        </Badge>
                                      </div>
                                      <div className="space-y-2 max-h-32 overflow-y-auto">
                                        {expandedDetail.notes?.length > 0 ? (
                                          expandedDetail.notes.map((note) => (
                                            <div key={note.note_id} className="text-xs p-2 rounded bg-muted/50">
                                              <p>{note.note}</p>
                                              <p className="text-[10px] text-muted-foreground mt-1">
                                                {note.created_by_name} • {formatDate(note.created_at?.split('T')[0])}
                                              </p>
                                            </div>
                                          ))
                                        ) : (
                                          <p className="text-xs text-muted-foreground">No notes yet</p>
                                        )}
                                      </div>
                                      {activeTab !== 'posted' && (
                                        <div className="flex gap-2 mt-2">
                                          <Input
                                            placeholder="Add a note..."
                                            value={newNote}
                                            onChange={(e) => setNewNote(e.target.value)}
                                            className="h-7 text-xs"
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                                          />
                                          <Button 
                                            size="sm" 
                                            className="h-7 text-xs px-2"
                                            onClick={handleAddNote}
                                            disabled={addingNote || !newNote.trim()}
                                          >
                                            {addingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Audit Trail */}
                                  {expandedDetail.audit_events?.length > 0 && (
                                    <motion.div 
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      transition={{ delay: 0.25 }}
                                      className="p-3 rounded-lg border bg-card"
                                    >
                                      <div className="flex items-center gap-2 mb-2">
                                        <Clock className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-xs font-medium">Audit Trail</span>
                                      </div>
                                      <div className="space-y-1.5">
                                        {expandedDetail.audit_events.slice(0, 5).map((event) => (
                                          <div key={event.event_id} className="flex items-center gap-2 text-[10px]">
                                            <span className="text-muted-foreground">{formatDate(event.timestamp?.split('T')[0])}</span>
                                            <Badge variant="outline" className="text-[10px] px-1 py-0">{event.action}</Badge>
                                            <span className="text-muted-foreground">by {event.user_name || 'System'}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </motion.div>
                                  )}
                                </div>
                              ) : (
                                <div className="p-4 text-center text-sm text-muted-foreground">
                                  Failed to load details
                                </div>
                              )}
                              </motion.div>
                            </TableCell>
                          </TableRow>
                          )}
                        </AnimatePresence>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-10 h-10 text-muted-foreground/50 mx-auto mb-2" />
                {hasHeaderFilters || debouncedSearch || dateFrom || dateTo || amountMin || amountMax ? (
                  <>
                    <p className="text-sm text-muted-foreground mb-1">
                      No transactions match your filters
                    </p>
                    <p className="text-xs text-muted-foreground mb-3">
                      Try adjusting your filter criteria or clearing filters to see all transactions.
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      {hasHeaderFilters && (
                        <Button variant="outline" size="sm" onClick={clearHeaderFilters} data-testid="empty-clear-header-filters">
                          Clear Status & Description Filters
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={clearFilters} data-testid="empty-clear-all-filters">
                        Clear All Filters
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      {activeTab === 'pending' && 'No pending transactions'}
                      {activeTab === 'posted' && 'No posted transactions'}
                      {activeTab === 'excluded' && 'No excluded transactions'}
                    </p>
                    {activeTab === 'pending' && bankAccounts.length === 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => window.dispatchEvent(new CustomEvent('open-setup-wizard', { detail: { startStep: 0 } }))}
                        data-testid="pending-connect-bank-btn"
                      >
                        Connect Bank Account
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-xs text-muted-foreground">
                  Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </Button>
                  <span className="text-xs px-2">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Entry Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="text-base">Create Journal Entry</SheetTitle>
            <SheetDescription className="text-xs">
              Categorize and post this bank transaction
            </SheetDescription>
          </SheetHeader>

          {selectedTransaction && (
            <div className="space-y-4 mt-4">
              {/* Transaction Summary */}
              <div className="p-3 rounded-lg bg-muted/50 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Description</span>
                  <span className="text-xs font-medium truncate max-w-[180px]">
                    {selectedTransaction.merchant_name || selectedTransaction.name}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Date</span>
                  <span className="text-xs font-mono">{formatDate(selectedTransaction.date)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Amount</span>
                  <span className={cn(
                    "font-mono font-semibold text-sm",
                    selectedTransaction.amount > 0 ? "text-red-600" : "text-emerald-600"
                  )}>
                    {formatCurrency(selectedTransaction.amount)}
                  </span>
                </div>
              </div>

              {/* Transaction Type */}
              <div className="space-y-1.5">
                <Label className="text-xs">Transaction Type</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'spend', label: 'Spend', icon: ArrowUpRight },
                    { value: 'receive', label: 'Receive', icon: ArrowDownLeft },
                    { value: 'transfer', label: 'Transfer', icon: ArrowLeftRight },
                  ].map(({ value, label, icon: Icon }) => (
                    <Button
                      key={value}
                      type="button"
                      variant={entryForm.transaction_type === value ? 'default' : 'outline'}
                      className="flex-col h-auto py-2"
                      size="sm"
                      onClick={() => setEntryForm({ ...entryForm, transaction_type: value })}
                    >
                      <Icon className="w-4 h-4 mb-0.5" />
                      <span className="text-[10px]">{label}</span>
                    </Button>
                  ))}
                </div>
              </div>

              {/* Account Selection */}
              <div className="space-y-1.5">
                <Label className="text-xs">Chart of Accounts *</Label>
                <SearchableAccountSelect
                  accounts={accounts}
                  value={entryForm.account_id}
                  onValueChange={(v) => setEntryForm({ ...entryForm, account_id: v })}
                  placeholder="Search accounts..."
                  className="h-8 text-xs"
                  testId="entry-account-select"
                  filterTypes={
                    entryForm.transaction_type === 'spend' 
                      ? ['expense', 'asset'] 
                      : entryForm.transaction_type === 'receive'
                        ? ['income', 'asset', 'liability']
                        : null
                  }
                />
              </div>

              {/* Vendor/Customer */}
              <div className="space-y-1.5">
                <Label className="text-xs">
                  {entryForm.transaction_type === 'receive' ? 'Customer' : 'Vendor'} (Optional)
                </Label>
                <Input
                  value={entryForm.customer_vendor}
                  onChange={(e) => setEntryForm({ ...entryForm, customer_vendor: e.target.value })}
                  placeholder={entryForm.transaction_type === 'receive' ? 'Customer name' : 'Vendor name'}
                  className="h-8 text-xs"
                />
              </div>

              {/* Memo */}
              <div className="space-y-1.5">
                <Label className="text-xs">Memo</Label>
                <Textarea
                  value={entryForm.memo}
                  onChange={(e) => setEntryForm({ ...entryForm, memo: e.target.value })}
                  placeholder="Description for the journal entry"
                  rows={2}
                  className="text-xs"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-3 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setDrawerOpen(false)}
                  className="flex-1 h-8 text-xs"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateEntry}
                  disabled={creatingEntry || !entryForm.account_id}
                  className="flex-1 h-8 text-xs"
                  data-testid="post-entry-btn"
                >
                  {creatingEntry ? (
                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Creating...</>
                  ) : (
                    <><CheckCircle2 className="w-3 h-3 mr-1" /> Post Entry</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Bulk Post Dialog */}
      <Dialog open={bulkActionDialog === 'post'} onOpenChange={() => setBulkActionDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">Bulk Post Transactions</DialogTitle>
            <DialogDescription className="text-xs">
              Create journal entries for {selectedIds.size} selected transactions
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Select Account for All Transactions</Label>
              <Select value={bulkAccountId} onValueChange={setBulkAccountId}>
                <SelectTrigger className="h-8 text-xs" data-testid="bulk-account-select">
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts
                    .filter(a => ['expense', 'income'].includes(a.type))
                    .map((account) => (
                      <SelectItem key={account.account_id} value={account.account_id} className="text-xs">
                        <span className="font-mono text-[10px]">{account.code}</span> - {account.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            <p className="text-[10px] text-muted-foreground">
              This will create one journal entry per transaction using the selected account.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setBulkActionDialog(null)}>
              Cancel
            </Button>
            <Button 
              size="sm"
              className="h-7 text-xs"
              onClick={handleBulkPost}
              disabled={bulkProcessing || !bulkAccountId}
              data-testid="confirm-bulk-post"
            >
              {bulkProcessing ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Processing...</>
              ) : (
                `Post ${selectedIds.size} Transactions`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Post Categorized Confirmation Dialog */}
      <Dialog open={bulkActionDialog === 'post-categorized'} onOpenChange={() => { setBulkActionDialog(null); setBulkPostPreview(null); }}>
        <DialogContent className="max-w-lg" data-testid="bulk-post-categorized-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Confirm Bulk Post
            </DialogTitle>
            <DialogDescription className="text-xs">
              Post categorized transactions to the general ledger
            </DialogDescription>
          </DialogHeader>
          
          {bulkPostPreview && (
            <div className="space-y-4 py-3">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                  <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium uppercase">Eligible to Post</p>
                  <p className="text-xl font-bold text-emerald-700 dark:text-emerald-300">{bulkPostPreview.eligible_count}</p>
                </div>
                {bulkPostPreview.ineligible_count > 0 && (
                  <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                    <p className="text-[10px] text-amber-600 dark:text-amber-400 font-medium uppercase">Will Skip</p>
                    <p className="text-xl font-bold text-amber-700 dark:text-amber-300">{bulkPostPreview.ineligible_count}</p>
                  </div>
                )}
              </div>

              {/* Amount Summary */}
              {bulkPostPreview.eligible_count > 0 && (
                <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                  <p className="text-xs font-medium">Amount Summary</p>
                  <div className="grid grid-cols-2 gap-4">
                    {bulkPostPreview.spend_count > 0 && (
                      <div className="flex items-center gap-2">
                        <ArrowUpRight className="w-4 h-4 text-red-500" />
                        <div>
                          <p className="text-[10px] text-muted-foreground">Spent ({bulkPostPreview.spend_count})</p>
                          <p className="text-sm font-mono font-medium text-red-600">{formatCurrency(bulkPostPreview.spend_total)}</p>
                        </div>
                      </div>
                    )}
                    {bulkPostPreview.receive_count > 0 && (
                      <div className="flex items-center gap-2">
                        <ArrowDownLeft className="w-4 h-4 text-emerald-500" />
                        <div>
                          <p className="text-[10px] text-muted-foreground">Received ({bulkPostPreview.receive_count})</p>
                          <p className="text-sm font-mono font-medium text-emerald-600">{formatCurrency(bulkPostPreview.receive_total)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Skipped Items Warning */}
              {bulkPostPreview.ineligible_count > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <p className="text-xs font-medium">Transactions that will be skipped:</p>
                  </div>
                  <ScrollArea className="h-[120px] rounded-md border">
                    <div className="p-2 space-y-1.5">
                      {bulkPostPreview.items
                        .filter(item => !item.eligible)
                        .map((item) => (
                          <div key={item.transaction_id} className="flex items-start justify-between gap-2 text-xs p-2 rounded bg-muted/30">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{item.description}</p>
                              <p className="text-[10px] text-muted-foreground">{formatCurrency(item.amount)}</p>
                            </div>
                            <Badge variant="outline" className="text-[10px] shrink-0 bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300 border-amber-200">
                              {item.skip_reason}
                            </Badge>
                          </div>
                        ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Eligible Items Preview */}
              {bulkPostPreview.eligible_count > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Transactions to post:</p>
                  <ScrollArea className="h-[120px] rounded-md border">
                    <div className="p-2 space-y-1.5">
                      {bulkPostPreview.items
                        .filter(item => item.eligible)
                        .map((item) => (
                          <div key={item.transaction_id} className="flex items-start justify-between gap-2 text-xs p-2 rounded bg-emerald-50/50 dark:bg-emerald-950/20">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{item.description}</p>
                              <p className="text-[10px] text-muted-foreground">
                                <span className="font-mono">{item.category_code}</span> - {item.category_name}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className={cn(
                                "font-mono text-xs font-medium",
                                item.direction === 'spend' ? 'text-red-600' : 'text-emerald-600'
                              )}>
                                {item.direction === 'spend' ? '-' : '+'}{formatCurrency(Math.abs(item.amount))}
                              </p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {bulkPostPreview.eligible_count === 0 && (
                <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-center">
                  <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-300">No eligible transactions</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    All selected transactions are either already posted, excluded, or missing a category.
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              size="sm" 
              className="h-7 text-xs" 
              onClick={() => { setBulkActionDialog(null); setBulkPostPreview(null); }}
            >
              Cancel
            </Button>
            <Button 
              size="sm"
              className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
              onClick={handleBulkPostCategorizedExecute}
              disabled={bulkProcessing || !bulkPostPreview || bulkPostPreview.eligible_count === 0}
              data-testid="confirm-bulk-post-categorized"
            >
              {bulkProcessing ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Posting...</>
              ) : (
                `Post ${bulkPostPreview?.eligible_count || 0} Transactions`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Categorize Dialog */}
      <Dialog open={bulkActionDialog === 'quick-categorize'} onOpenChange={() => { setBulkActionDialog(null); setQuickCategorizePreview(null); setQuickCategorizeAccountId(''); }}>
        <DialogContent className="max-w-lg" data-testid="quick-categorize-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Tags className="w-5 h-5 text-blue-600" />
              Quick Categorize
            </DialogTitle>
            <DialogDescription className="text-xs">
              Assign a category to multiple uncategorized transactions at once
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-3">
            {/* Category Selection */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Category Account *</Label>
              <SearchableAccountSelect
                accounts={accounts}
                value={quickCategorizeAccountId}
                onValueChange={handleQuickCategorizePreview}
                placeholder="Search category account..."
                className="h-9 text-xs"
                testId="quick-categorize-account-select"
                filterTypes={['expense', 'income', 'asset', 'liability']}
              />
            </div>

            {/* Optional Contact */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Vendor/Customer (Optional)</Label>
              <Select 
                value={quickCategorizeContactId} 
                onValueChange={setQuickCategorizeContactId}
              >
                <SelectTrigger className="h-9 text-xs" data-testid="quick-categorize-contact-select">
                  <SelectValue placeholder="Select vendor or customer..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" className="text-xs text-muted-foreground">None</SelectItem>
                  {contacts.map((contact) => (
                    <SelectItem key={contact.contact_id} value={contact.contact_id} className="text-xs">
                      {contact.name}
                      <Badge variant="outline" className="ml-2 text-[9px]">{contact.type}</Badge>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Optional Memo */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Memo (Optional)</Label>
              <Input
                value={quickCategorizeMemo}
                onChange={(e) => setQuickCategorizeMemo(e.target.value)}
                placeholder="Add a note to all transactions..."
                className="h-9 text-xs"
                data-testid="quick-categorize-memo"
              />
            </div>

            {/* Preview Loading */}
            {quickCategorizeLoading && (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                <span className="ml-2 text-xs text-muted-foreground">Checking eligibility...</span>
              </div>
            )}

            {/* Preview Results */}
            {quickCategorizePreview && !quickCategorizeLoading && (
              <div className="space-y-4">
                {/* Target Category Info */}
                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                  <p className="text-[10px] text-blue-600 dark:text-blue-400 font-medium uppercase">Assigning to</p>
                  <p className="text-sm font-medium">
                    <span className="font-mono text-xs text-muted-foreground">{quickCategorizePreview.target_account_code}</span>
                    <span className="ml-1">{quickCategorizePreview.target_account_name}</span>
                  </p>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                    <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium uppercase">Eligible</p>
                    <p className="text-xl font-bold text-emerald-700 dark:text-emerald-300">{quickCategorizePreview.eligible_count}</p>
                  </div>
                  {quickCategorizePreview.ineligible_count > 0 && (
                    <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                      <p className="text-[10px] text-amber-600 dark:text-amber-400 font-medium uppercase">Will Skip</p>
                      <p className="text-xl font-bold text-amber-700 dark:text-amber-300">{quickCategorizePreview.ineligible_count}</p>
                    </div>
                  )}
                </div>

                {/* Amount Summary */}
                {quickCategorizePreview.eligible_count > 0 && (
                  <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                    <p className="text-xs font-medium">Amount Summary</p>
                    <div className="grid grid-cols-2 gap-4">
                      {quickCategorizePreview.spend_count > 0 && (
                        <div className="flex items-center gap-2">
                          <ArrowUpRight className="w-4 h-4 text-red-500" />
                          <div>
                            <p className="text-[10px] text-muted-foreground">Spent ({quickCategorizePreview.spend_count})</p>
                            <p className="text-sm font-mono font-medium text-red-600">{formatCurrency(quickCategorizePreview.spend_total)}</p>
                          </div>
                        </div>
                      )}
                      {quickCategorizePreview.receive_count > 0 && (
                        <div className="flex items-center gap-2">
                          <ArrowDownLeft className="w-4 h-4 text-emerald-500" />
                          <div>
                            <p className="text-[10px] text-muted-foreground">Received ({quickCategorizePreview.receive_count})</p>
                            <p className="text-sm font-mono font-medium text-emerald-600">{formatCurrency(quickCategorizePreview.receive_total)}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Skipped Items */}
                {quickCategorizePreview.ineligible_count > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                      <AlertCircle className="w-3.5 h-3.5" />
                      <p className="text-xs font-medium">Transactions that will be skipped:</p>
                    </div>
                    <ScrollArea className="h-[100px] rounded-md border">
                      <div className="p-2 space-y-1.5">
                        {quickCategorizePreview.items
                          .filter(item => !item.eligible)
                          .map((item) => (
                            <div key={item.transaction_id} className="flex items-start justify-between gap-2 text-xs p-2 rounded bg-muted/30">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{item.description}</p>
                                {item.current_category && (
                                  <p className="text-[10px] text-muted-foreground">Current: {item.current_category}</p>
                                )}
                              </div>
                              <Badge variant="outline" className="text-[10px] shrink-0 bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300 border-amber-200">
                                {item.skip_reason}
                              </Badge>
                            </div>
                          ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}

                {/* Eligible Items */}
                {quickCategorizePreview.eligible_count > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Transactions to categorize:</p>
                    <ScrollArea className="h-[100px] rounded-md border">
                      <div className="p-2 space-y-1.5">
                        {quickCategorizePreview.items
                          .filter(item => item.eligible)
                          .map((item) => (
                            <div key={item.transaction_id} className="flex items-start justify-between gap-2 text-xs p-2 rounded bg-emerald-50/50 dark:bg-emerald-950/20">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{item.description}</p>
                              </div>
                              <p className={cn(
                                "font-mono text-xs font-medium shrink-0",
                                item.direction === 'spend' ? 'text-red-600' : 'text-emerald-600'
                              )}>
                                {item.direction === 'spend' ? '-' : '+'}{formatCurrency(Math.abs(item.amount))}
                              </p>
                            </div>
                          ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}

                {/* No eligible warning */}
                {quickCategorizePreview.eligible_count === 0 && (
                  <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-center">
                    <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-300">No eligible transactions</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      All selected transactions are already categorized, posted, or excluded.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Info text when no account selected */}
            {!quickCategorizeAccountId && !quickCategorizeLoading && (
              <p className="text-xs text-muted-foreground text-center py-4">
                Select a category account to see which transactions can be categorized.
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              size="sm" 
              className="h-7 text-xs" 
              onClick={() => { setBulkActionDialog(null); setQuickCategorizePreview(null); setQuickCategorizeAccountId(''); }}
            >
              Cancel
            </Button>
            <Button 
              size="sm"
              className="h-7 text-xs"
              onClick={handleQuickCategorizeExecute}
              disabled={bulkProcessing || !quickCategorizePreview || quickCategorizePreview.eligible_count === 0}
              data-testid="confirm-quick-categorize"
            >
              {bulkProcessing ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Categorizing...</>
              ) : (
                `Categorize ${quickCategorizePreview?.eligible_count || 0} Transactions`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split Transaction Drawer */}
      <Sheet open={splitDrawerOpen} onOpenChange={(open) => !open && closeSplitDrawer()}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-base">Split Transaction</SheetTitle>
            <SheetDescription className="text-xs">
              Split this transaction into multiple categories
            </SheetDescription>
          </SheetHeader>

          {splitTransaction && (
            <div className="space-y-4 mt-4">
              {/* Original Transaction Header */}
              <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium">{splitTransaction.merchant_name || splitTransaction.name}</p>
                    <p className="text-[10px] text-muted-foreground">{formatDate(splitTransaction.date)}</p>
                  </div>
                  <div className="text-right">
                    <div className={cn(
                      "flex items-center gap-1",
                      splitTransaction.amount > 0 ? "text-red-600" : "text-emerald-600"
                    )}>
                      {splitTransaction.amount > 0 ? (
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowDownLeft className="w-3.5 h-3.5" />
                      )}
                      <span className="font-mono font-bold">{formatCurrency(splitTransaction.amount)}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      {splitTransaction.amount > 0 ? 'Spent' : 'Received'}
                    </p>
                  </div>
                </div>
              </div>

              {splitLoading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-16" />
                  ))}
                </div>
              ) : (
                <>
                  {/* Split Lines Table */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium">Split Lines</Label>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-7 text-xs"
                        onClick={addSplitLine}
                      >
                        <PlusCircle className="w-3 h-3 mr-1" />
                        Add Line
                      </Button>
                    </div>

                    <div className="space-y-2">
                      {splitLines.map((line, idx) => (
                        <div 
                          key={line.id} 
                          className="p-3 rounded-lg border bg-card space-y-2"
                          data-testid={`split-line-${idx}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-muted-foreground font-medium">
                              Line {idx + 1}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5"
                              onClick={() => removeSplitLine(line.id)}
                              disabled={splitLines.length <= 2}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-[10px]">Account *</Label>
                              <SearchableAccountSelect
                                accounts={accounts}
                                value={line.account_id}
                                onValueChange={(v) => updateSplitLine(line.id, 'account_id', v)}
                                placeholder="Search..."
                                className="h-7 text-xs"
                                testId={`split-line-account-${idx}`}
                                filterTypes={
                                  splitTransaction.amount > 0 
                                    ? ['expense', 'asset', 'liability']
                                    : ['income', 'asset', 'liability']
                                }
                              />
                            </div>
                            
                            <div className="space-y-1">
                              <Label className="text-[10px]">Amount *</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={line.amount}
                                onChange={(e) => updateSplitLine(line.id, 'amount', e.target.value)}
                                placeholder="0.00"
                                className="h-7 text-xs font-mono"
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-[10px]">
                                {splitTransaction.amount > 0 ? 'Vendor' : 'Customer'} (optional)
                              </Label>
                              <Select 
                                value={line.contact_id || 'none'} 
                                onValueChange={(v) => updateSplitLine(line.id, 'contact_id', v === 'none' ? '' : v)}
                              >
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  {contacts
                                    .filter(c => {
                                      if (splitTransaction.amount > 0) {
                                        return c.type === 'vendor' || c.type === 'both';
                                      } else {
                                        return c.type === 'customer' || c.type === 'both';
                                      }
                                    })
                                    .map((contact) => (
                                      <SelectItem key={contact.contact_id} value={contact.contact_id} className="text-xs">
                                        {contact.name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="space-y-1">
                              <Label className="text-[10px]">Memo (optional)</Label>
                              <Input
                                value={line.memo}
                                onChange={(e) => updateSplitLine(line.id, 'memo', e.target.value)}
                                placeholder="Description"
                                className="h-7 text-xs"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Running Total and Remaining */}
                  <div className="p-3 rounded-lg border bg-muted/30 space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Original Amount:</span>
                      <span className="font-mono font-medium">{formatCurrency(splitTransaction.amount)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Split Total:</span>
                      <span className="font-mono font-medium">{formatCurrency(splitTotal)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs border-t pt-1.5">
                      <span className="font-medium">Remaining:</span>
                      <span className={cn(
                        "font-mono font-bold",
                        Math.abs(splitRemaining) < 0.01 ? "text-emerald-600" : "text-red-600"
                      )}>
                        {formatCurrency(splitRemaining)}
                      </span>
                    </div>
                    {Math.abs(splitRemaining) >= 0.01 && (
                      <p className="text-[10px] text-red-600 flex items-center gap-1 mt-1">
                        <AlertCircle className="w-3 h-3" />
                        Remaining must be $0.00 to save
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="space-y-2 pt-3 border-t">
                    {existingSplit && existingSplit.status === 'draft' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={deleteSplit}
                      >
                        <X className="w-3.5 h-3.5 mr-1" />
                        Delete Split Draft
                      </Button>
                    )}
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={closeSplitDrawer}
                        className="flex-1 h-8 text-xs"
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => saveSplit(false)}
                        disabled={splitSaving || splitPosting || !isSplitValid}
                        className="flex-1 h-8 text-xs"
                        data-testid="save-split-draft"
                      >
                        {splitSaving ? (
                          <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving...</>
                        ) : (
                          'Save for Review'
                        )}
                      </Button>
                    </div>
                    
                    <Button 
                      onClick={() => saveSplit(true)}
                      disabled={splitSaving || splitPosting || !isSplitValid}
                      className="w-full h-9 text-xs"
                      data-testid="split-and-post"
                    >
                      {splitPosting ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Posting...</>
                      ) : (
                        <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Split & Post</>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Add Account Drawer */}
      <Sheet open={addAccountDrawerOpen} onOpenChange={(open) => !open && closeAddAccountDrawer()}>
        <SheetContent className="sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-base">
              {addAccountStep === 'choose' ? 'Add Bank Account' : 'Add Manual Account'}
            </SheetTitle>
            <SheetDescription className="text-xs">
              {addAccountStep === 'choose' 
                ? 'Choose how to add your bank account'
                : 'Enter your account details manually'}
            </SheetDescription>
          </SheetHeader>

          {addAccountStep === 'choose' ? (
            <div className="space-y-4 mt-4">
              {/* Connect with Plaid */}
              <button
                onClick={() => {
                  closeAddAccountDrawer();
                  // Open the SetupWizard at step 0 (Connect bank) — it has the
                  // full Plaid Link flow, plan-limit handling, and toast UX.
                  window.dispatchEvent(new CustomEvent('open-setup-wizard', { detail: { startStep: 0 } }));
                }}
                className="w-full p-4 rounded-lg border hover:border-primary/50 hover:bg-primary/5 transition-all text-left"
                data-testid="connect-plaid-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                    <Landmark className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Connect with Plaid</p>
                    <p className="text-xs text-muted-foreground">
                      Securely link your bank for automatic transaction sync
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-2 text-xs text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="w-3 h-3" />
                  <span>Automatic sync</span>
                  <CheckCircle2 className="w-3 h-3 ml-2" />
                  <span>Real-time balance</span>
                </div>
              </button>

              {/* Add Manual Account */}
              <button
                onClick={() => setAddAccountStep('manual')}
                className="w-full p-4 rounded-lg border hover:border-primary/50 hover:bg-primary/5 transition-all text-left"
                data-testid="add-manual-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                    <Wallet className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Add Manual Account</p>
                    <p className="text-xs text-muted-foreground">
                      Track cash, petty cash, or accounts without Plaid
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-2 text-xs text-amber-600 dark:text-amber-400">
                  <HandCoins className="w-3 h-3" />
                  <span>Manual transactions</span>
                  <CircleDollarSign className="w-3 h-3 ml-2" />
                  <span>Opening balance JE</span>
                </div>
              </button>
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              {/* Back button */}
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 text-xs -ml-2"
                onClick={() => setAddAccountStep('choose')}
              >
                <ChevronLeft className="w-3 h-3 mr-1" />
                Back
              </Button>

              {/* Account Name */}
              <div className="space-y-1.5">
                <Label className="text-xs">Account Name *</Label>
                <Input
                  value={manualAccountForm.account_name}
                  onChange={(e) => setManualAccountForm({...manualAccountForm, account_name: e.target.value})}
                  placeholder="e.g., Petty Cash, Business Cash"
                  className="h-8 text-xs"
                />
              </div>

              {/* Account Type */}
              <div className="space-y-1.5">
                <Label className="text-xs">Account Type *</Label>
                <Select 
                  value={manualAccountForm.account_type} 
                  onValueChange={(v) => setManualAccountForm({...manualAccountForm, account_type: v})}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checking">Checking</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Currency */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Currency</Label>
                  <Select 
                    value={manualAccountForm.currency} 
                    onValueChange={(v) => setManualAccountForm({...manualAccountForm, currency: v})}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                      <SelectItem value="CAD">CAD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Opening Balance */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Opening Balance *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={manualAccountForm.opening_balance}
                    onChange={(e) => setManualAccountForm({...manualAccountForm, opening_balance: e.target.value})}
                    placeholder="0.00"
                    className="h-8 text-xs font-mono"
                  />
                </div>
              </div>

              {/* Opening Balance Date */}
              <div className="space-y-1.5">
                <Label className="text-xs">Opening Balance Date *</Label>
                <Input
                  type="date"
                  value={manualAccountForm.opening_balance_date}
                  onChange={(e) => setManualAccountForm({...manualAccountForm, opening_balance_date: e.target.value})}
                  className="h-8 text-xs"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Institution Name (Optional) */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Institution Name</Label>
                  <Input
                    value={manualAccountForm.institution_name}
                    onChange={(e) => setManualAccountForm({...manualAccountForm, institution_name: e.target.value})}
                    placeholder="Optional"
                    className="h-8 text-xs"
                  />
                </div>

                {/* Last 4 / Mask (Optional) */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Last 4 Digits</Label>
                  <Input
                    value={manualAccountForm.mask}
                    onChange={(e) => setManualAccountForm({...manualAccountForm, mask: e.target.value.slice(0, 4)})}
                    placeholder="1234"
                    maxLength={4}
                    className="h-8 text-xs font-mono"
                  />
                </div>
              </div>

              {/* GL Account Mapping */}
              <div className="space-y-1.5">
                <Label className="text-xs">GL Cash Account (Optional)</Label>
                <Select 
                  value={manualAccountForm.gl_account_id || 'create_new'} 
                  onValueChange={(v) => {
                    if (v === 'create_new') {
                      setManualAccountForm({...manualAccountForm, gl_account_id: '', create_gl_account: true});
                    } else {
                      setManualAccountForm({...manualAccountForm, gl_account_id: v, create_gl_account: false});
                    }
                  }}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Select or create new" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="create_new">
                      <span className="text-primary">+ Create new GL account</span>
                    </SelectItem>
                    {accounts
                      .filter(a => a.type === 'asset' && (a.subtype === 'bank' || a.code?.startsWith('1')))
                      .map((account) => (
                        <SelectItem key={account.account_id} value={account.account_id} className="text-xs">
                          <span className="font-mono text-[10px]">{account.code}</span> - {account.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">
                  Maps to Chart of Accounts for ledger entries
                </p>
              </div>

              {/* Info Box */}
              <div className="p-3 rounded-lg bg-muted/50 border">
                <p className="text-[10px] text-muted-foreground">
                  <strong>Note:</strong> An opening balance journal entry will be automatically created 
                  to debit/credit the GL cash account and offset to Opening Balance Equity.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-3 border-t">
                <Button 
                  variant="outline" 
                  onClick={closeAddAccountDrawer}
                  className="flex-1 h-8 text-xs"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={createManualAccount}
                  disabled={creatingManualAccount || !manualAccountForm.account_name || !manualAccountForm.opening_balance}
                  className="flex-1 h-8 text-xs"
                  data-testid="save-manual-account"
                >
                  {creatingManualAccount ? (
                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Creating...</>
                  ) : (
                    <><CheckCircle2 className="w-3 h-3 mr-1" /> Create Account</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Manual Transaction Drawer */}
      <Sheet open={manualTxnDrawerOpen} onOpenChange={(open) => !open && closeManualTxnDrawer()}>
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="text-base">Create Manual Transaction</SheetTitle>
            <SheetDescription className="text-xs">
              Add a transaction to your manual account
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4 mt-4">
            {/* Transaction Type */}
            <div className="space-y-1.5">
              <Label className="text-xs">Transaction Type *</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={manualTxnForm.transaction_type === 'spend' ? 'default' : 'outline'}
                  className="flex-col h-auto py-2"
                  size="sm"
                  onClick={() => setManualTxnForm({...manualTxnForm, transaction_type: 'spend'})}
                >
                  <ArrowUpRight className="w-4 h-4 mb-0.5 text-red-500" />
                  <span className="text-[10px]">Spent (Outflow)</span>
                </Button>
                <Button
                  type="button"
                  variant={manualTxnForm.transaction_type === 'receive' ? 'default' : 'outline'}
                  className="flex-col h-auto py-2"
                  size="sm"
                  onClick={() => setManualTxnForm({...manualTxnForm, transaction_type: 'receive'})}
                >
                  <ArrowDownLeft className="w-4 h-4 mb-0.5 text-emerald-500" />
                  <span className="text-[10px]">Received (Inflow)</span>
                </Button>
              </div>
            </div>

            {/* Date */}
            <div className="space-y-1.5">
              <Label className="text-xs">Date *</Label>
              <Input
                type="date"
                value={manualTxnForm.date}
                onChange={(e) => setManualTxnForm({...manualTxnForm, date: e.target.value})}
                className="h-8 text-xs"
              />
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Label className="text-xs">Description *</Label>
              <Input
                value={manualTxnForm.description}
                onChange={(e) => setManualTxnForm({...manualTxnForm, description: e.target.value})}
                placeholder="e.g., Office supplies, Client payment"
                className="h-8 text-xs"
              />
            </div>

            {/* Amount */}
            <div className="space-y-1.5">
              <Label className="text-xs">Amount *</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={manualTxnForm.amount}
                onChange={(e) => setManualTxnForm({...manualTxnForm, amount: e.target.value})}
                placeholder="0.00"
                className="h-8 text-xs font-mono"
              />
            </div>

            {/* Merchant Name (Optional) */}
            <div className="space-y-1.5">
              <Label className="text-xs">Merchant/Payee</Label>
              <Input
                value={manualTxnForm.merchant_name}
                onChange={(e) => setManualTxnForm({...manualTxnForm, merchant_name: e.target.value})}
                placeholder="Optional"
                className="h-8 text-xs"
              />
            </div>

            {/* Memo (Optional) */}
            <div className="space-y-1.5">
              <Label className="text-xs">Memo</Label>
              <Textarea
                value={manualTxnForm.memo}
                onChange={(e) => setManualTxnForm({...manualTxnForm, memo: e.target.value})}
                placeholder="Additional notes..."
                rows={2}
                className="text-xs"
              />
            </div>

            {/* Category Account (Optional) */}
            <div className="space-y-1.5">
              <Label className="text-xs">Category Account (Optional)</Label>
              <Select 
                value={manualTxnForm.category_account_id || 'none'} 
                onValueChange={(v) => setManualTxnForm({...manualTxnForm, category_account_id: v === 'none' ? '' : v})}
              >
                <SelectTrigger className="h-8 text-xs" data-testid="manual-txn-category">
                  <SelectValue placeholder="Leave blank for AI Buck" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" className="text-xs text-muted-foreground">
                    No category (route to AI Buck)
                  </SelectItem>
                  {filteredCategoryAccounts.map((account) => (
                    <SelectItem key={account.account_id} value={account.account_id} className="text-xs">
                      <span className="font-mono text-[10px]">{account.code}</span> - {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">
                {manualTxnForm.transaction_type === 'spend' 
                  ? 'Expense, asset, or liability accounts' 
                  : 'Income, asset, or liability accounts'}
              </p>
            </div>

            {/* Vendor/Customer (Optional) */}
            <div className="space-y-1.5">
              <Label className="text-xs">
                {manualTxnForm.transaction_type === 'spend' ? 'Vendor' : 'Customer'} (Optional)
              </Label>
              <Select 
                value={manualTxnForm.contact_id || 'none'} 
                onValueChange={(v) => setManualTxnForm({...manualTxnForm, contact_id: v === 'none' ? '' : v})}
              >
                <SelectTrigger className="h-8 text-xs" data-testid="manual-txn-contact">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" className="text-xs text-muted-foreground">
                    None
                  </SelectItem>
                  {filteredContacts.map((contact) => (
                    <SelectItem key={contact.contact_id} value={contact.contact_id} className="text-xs">
                      {contact.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Info Box */}
            <div className="p-3 rounded-lg bg-muted/50 border">
              <p className="text-[10px] text-muted-foreground">
                {manualTxnForm.category_account_id 
                  ? 'Transaction will be pre-categorized and ready for posting.'
                  : 'Transaction will appear in AI Buck queue for automatic categorization suggestions.'}
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-3 border-t">
              <Button 
                variant="outline" 
                onClick={closeManualTxnDrawer}
                className="flex-1 h-8 text-xs"
              >
                Cancel
              </Button>
              <Button 
                onClick={createManualTransaction}
                disabled={creatingManualTxn || !manualTxnForm.description || !manualTxnForm.amount}
                className="flex-1 h-8 text-xs"
                data-testid="create-manual-txn"
              >
                {creatingManualTxn ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Creating...</>
                ) : (
                  <><CheckCircle2 className="w-3 h-3 mr-1" /> Create Transaction</>
                )}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Document Scanner Dialog */}
      <Dialog open={scannerDialogOpen} onOpenChange={setScannerDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="w-5 h-5 text-emerald-600" />
              Scan Bank Statement (AI)
            </DialogTitle>
            <DialogDescription>
              Upload a bank statement PDF or image. AI will extract transactions for review before importing.
            </DialogDescription>
          </DialogHeader>
          {currentWorkspace && (
            <DocumentScanner
              workspaceId={currentWorkspace.workspace_id}
              docType="bank_statement"
              onImportSuccess={() => {
                setScannerDialogOpen(false);
                fetchTransactions();
                fetchAccounts();
                toast.success('Transactions imported from scan');
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Apply-to-Similar follow-up prompt */}
      <Dialog open={!!similarPrompt} onOpenChange={(open) => { if (!open) setSimilarPrompt(null); }}>
        <DialogContent className="max-w-2xl" data-testid="similar-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-500" />
              Apply same category to similar transactions?
            </DialogTitle>
            <DialogDescription>
              {similarPrompt && (
                <>
                  We found <strong className="text-foreground">{similarPrompt.items.length}</strong>{' '}
                  uncategorized transaction{similarPrompt.items.length === 1 ? '' : 's'} from{' '}
                  <strong className="text-foreground">&quot;{similarPrompt.merchant}&quot;</strong>.
                  Apply{' '}
                  <strong className="text-foreground">{similarPrompt.accountName}</strong>{' '}
                  to {similarPrompt.items.length === 1 ? 'it' : 'all of them'}
                  {similarPrompt.mode === 'post' ? ' and post the journal entries' : ''}?
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto rounded-md border border-border bg-muted/30 p-2 text-sm" data-testid="similar-prompt-list">
            {similarPrompt?.items?.slice(0, 25).map((it) => (
              <div
                key={it.transaction_id}
                className="flex items-center justify-between py-1 px-2 border-b last:border-b-0 border-border/50"
                data-testid={`similar-item-${it.transaction_id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{it.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {it.date}{it.bank_name ? ` · ${it.bank_name}` : ''}
                  </p>
                </div>
                <span className={`font-mono text-sm ${it.amount > 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
                  {it.amount > 0 ? '−' : '+'}${Math.abs(it.amount).toFixed(2)}
                </span>
              </div>
            ))}
            {similarPrompt?.items?.length > 25 && (
              <p className="text-xs text-muted-foreground py-2 px-2 italic">
                +{similarPrompt.items.length - 25} more…
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setSimilarPrompt(null)}
              disabled={similarProcessing}
              data-testid="similar-prompt-skip"
            >
              Skip
            </Button>
            <Button
              onClick={handleApplySimilar}
              disabled={similarProcessing}
              data-testid="similar-prompt-apply"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {similarProcessing
                ? 'Applying…'
                : similarPrompt?.mode === 'post'
                  ? `Apply & post ${similarPrompt?.items?.length || 0}`
                  : `Apply to ${similarPrompt?.items?.length || 0}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
